import { useRef, useEffect, useState } from 'react';
import maplibregl from 'maplibre-gl';
import { Protocol } from 'pmtiles';
import { ADMIN_CLEAR_KEEP_CAMERA_ABOVE_ZOOM, MAP_BASE_STYLE, MAP_MIN_LOT_ZOOM } from '../styles/mapTheme';
import { PROVINCE_LABEL_GEOJSON } from '../data/provinceLabelsGeojson';
import { moveProvinceLayersToTop, moveBaseLabelsToTop } from '../mapLayerOrder';
import { getProvinceManualBounds, getProvinceFitMaxZoomOverride } from '../provinceClickBounds';
import {
  buildProvinceHoverFilter,
  getProvinceIdentityForFilter,
  PROVINCE_HOVER_INACTIVE,
} from '../provinceVectorProperty';
import { getSignedPmtilesUrl, refreshPmtilesVectorSourceIfNeeded } from '../../Utils/pmtilesAuth';
import { canAccessMapStyle, isMapAlive } from '../utils/canAccessMapStyle';
import { registerPmtilesProtocol } from '../utils/pmtilesProtocol';
import { probeIgnBasemap } from '../utils/ignBasemapProbe';
import { toast } from '../../Components/Toast';
import {
  getWebGLSupport,
  isWebGLAvailable,
  markWebGLUnavailable,
  reportWebGLUnavailableOnce,
} from '../utils/webglSupport';
import { cleanupFailedMapContainer } from '../utils/cleanupFailedMapContainer';
import { isEmbeddedFrame } from '../../Utils/isEmbeddedFrame';

// Registro del protocolo PMTiles — una sola vez al cargar el módulo.
// Con wrapper: no responde a pedidos ya cancelados (ver utils/pmtilesProtocol).
registerPmtilesProtocol(maplibregl, new Protocol());

const PROVINCES_LOOKUP_SOURCE_ID = 'provinces-lookup';
const PROVINCES_LOOKUP_LAYER_ID = 'provinces-lookup-hit';
const PROVINCES_ORIGINAL_URL =
  'pmtiles://https://s3.us-east-1.amazonaws.com/argenlot.lots/tiles/provinces/provinces.pmtiles';
const DEPARTMENTS_URL_PREFIX =
  'pmtiles://https://s3.us-east-1.amazonaws.com/argenlot.lots/tiles';

const ACTIVE_DEPARTMENT_SOURCE_ID = 'active-departments';
const ACTIVE_DEPARTMENT_HIT_LAYER_ID = 'active-departments-hit';
const ACTIVE_DEPARTMENT_BORDERS_LAYER_ID = 'active-departments-borders';
const ACTIVE_DEPARTMENT_HOVER_FILL_LAYER_ID = 'active-departments-hover-fill';
const ACTIVE_DEPARTMENT_HOVER_LAYER_ID = 'active-departments-hover';
const ACTIVE_DEPARTMENT_SELECTED_LAYER_ID = 'active-departments-selected';
const ACTIVE_MUNICIPALITY_SOURCE_ID = 'active-municipalities';
const ACTIVE_MUNICIPALITY_HIT_LAYER_ID = 'active-municipalities-hit';
const ACTIVE_MUNICIPALITY_BORDERS_LAYER_ID = 'active-municipalities-borders';
const ACTIVE_MUNICIPALITY_HOVER_FILL_LAYER_ID = 'active-municipalities-hover-fill';
const ACTIVE_MUNICIPALITY_HOVER_LAYER_ID = 'active-municipalities-hover';
const ACTIVE_MUNICIPALITY_SELECTED_LAYER_ID = 'active-municipalities-selected';
const ACTIVE_PEDANIA_SOURCE_ID = 'active-pedanias';
const ACTIVE_PEDANIA_HIT_LAYER_ID = 'active-pedanias-hit';
const ACTIVE_PEDANIA_BORDERS_LAYER_ID = 'active-pedanias-borders';
const ACTIVE_PEDANIA_HOVER_FILL_LAYER_ID = 'active-pedanias-hover-fill';
const ACTIVE_PEDANIA_HOVER_LAYER_ID = 'active-pedanias-hover';
const ACTIVE_PEDANIA_SELECTED_LAYER_ID = 'active-pedanias-selected';
const ACTIVE_NEIGHBORHOOD_SOURCE_ID = 'active-neighborhoods';
const ACTIVE_NEIGHBORHOOD_HIT_LAYER_ID = 'active-neighborhoods-hit';
const ACTIVE_NEIGHBORHOOD_BORDERS_LAYER_ID = 'active-neighborhoods-borders';
const ACTIVE_NEIGHBORHOOD_LABELS_LAYER_ID = 'active-neighborhoods-labels';
const ACTIVE_NEIGHBORHOOD_LABELS_SOURCE_ID = 'active-neighborhood-labels-src';
const DEPARTMENT_SOURCE_LAYER_CANDIDATES = [
  'departments',
  'department',
  'deptos',
  'depto',
  'layer0',
];
const MUNICIPALITY_SOURCE_LAYER_CANDIDATES = [
  'municipalities',
  'municipality',
  'municipios',
  'municipio',
  'layer0',
];
const PEDANIA_SOURCE_LAYER_CANDIDATES = [
  'pedanias',
  'pedania',
  'pedanías',
  'layer0',
];
const NEIGHBORHOOD_SOURCE_LAYER_CANDIDATES = [
  'neighborhoods',
  'neighborhood',
  'barrios',
  'barrio',
  'layer0',
];
const PROVINCE_ADMIN_LAYER_CAPABILITIES = {
  // Córdoba tiene granularidad adicional entre departamento y municipio.
  cordoba: {
    pedanias: true,
    municipalities: true,
    neighborhoods: true,
  },
};
const FILTER_NEVER_MATCH = ['==', ['get', '__terrio_hover__'], '__on__'];
const DEPARTMENT_TOOLTIP_MAX_ZOOM = 11;
const CABA_DEPARTMENT_TOOLTIP_MAX_ZOOM = 13;
const MUNICIPALITY_TOOLTIP_MAX_ZOOM = 12;
const CABA_MUNICIPALITY_TOOLTIP_MAX_ZOOM = 13;
const PROVINCE_HOVER_MAX_ZOOM = 9;
const NEIGHBORHOOD_NAME_RENDER_MODE = 'labels'; // 'labels' | 'tooltip'
const NEIGHBORHOOD_NAMES_MIN_ZOOM = 10;
const NEIGHBORHOOD_NAMES_MAX_ZOOM = 24;
// const NEIGHBORHOOD_NAMES_MAX_ZOOM = Math.max(MAP_MIN_LOT_ZOOM + 3.5, MAP_MIN_LOT_ZOOM + 0.1);

function getDepartmentTooltipMaxZoom(activeProvinceSlug) {
  return activeProvinceSlug === 'ciudad_autonoma_de_buenos_aires' || activeProvinceSlug === 'caba'
    ? CABA_DEPARTMENT_TOOLTIP_MAX_ZOOM
    : DEPARTMENT_TOOLTIP_MAX_ZOOM;
}

function getMunicipalityTooltipMaxZoom(activeProvinceSlug) {
  return activeProvinceSlug === 'ciudad_autonoma_de_buenos_aires' || activeProvinceSlug === 'caba'
    ? CABA_MUNICIPALITY_TOOLTIP_MAX_ZOOM
    : MUNICIPALITY_TOOLTIP_MAX_ZOOM;
}
const INITIAL_MAP_VIEW = { center: [-65, -38], zoom: 2 };

function debounce(fn, ms) {
  let t;
  return (...args) => {
    clearTimeout(t);
    t = setTimeout(() => fn(...args), ms);
  };
}

/**
 * BBOX del polígono tal como viene del tile: recorre coordenadas (outer rings; incluye islas en MultiPolygon).
 */
function boundsFromFeatureGeometry(geometry) {
  if (!geometry) return null;
  const b = new maplibregl.LngLatBounds();
  const addCoord = (c) => {
    if (Array.isArray(c) && c.length >= 2 && typeof c[0] === 'number' && typeof c[1] === 'number') {
      b.extend(/** @type {[number, number]} */ (c));
    } else if (Array.isArray(c)) {
      c.forEach((x) => addCoord(x));
    }
  };
  if (geometry.type === 'Polygon' || geometry.type === 'MultiPolygon') {
    addCoord(geometry.coordinates);
  } else {
    return null;
  }
  return b;
}

/** Primera feature de provincia bajo el punto: hit (fill) o, si no hay, borde. */
function queryProvinceFeatureAtPoint(map, point) {
  if (!map.getLayer('provinces-admin-hit-area')) return null;
  let hits = map.queryRenderedFeatures(point, { layers: ['provinces-admin-hit-area'] });
  if (hits.length) return hits[0];
  if (map.getLayer('provinces-admin-borders')) {
    hits = map.queryRenderedFeatures(point, { layers: ['provinces-admin-borders'] });
  }
  return hits[0] || null;
}

/** Ray casting. El anillo es `[[lng, lat], ...]`. */
function ringContainsLngLat(ring, lng, lat) {
  if (!Array.isArray(ring) || ring.length < 3) return false;
  let inside = false;
  for (let i = 0, j = ring.length - 1; i < ring.length; j = i, i += 1) {
    const xi = ring[i][0];
    const yi = ring[i][1];
    const xj = ring[j][0];
    const yj = ring[j][1];
    const crosses = (yi > lat) !== (yj > lat);
    if (crosses && (lng < ((xj - xi) * (lat - yi)) / (yj - yi) + xi)) inside = !inside;
  }
  return inside;
}

function polygonContainsLngLat(polygon, lng, lat) {
  if (!Array.isArray(polygon) || !polygon.length) return false;
  if (!ringContainsLngLat(polygon[0], lng, lat)) return false;
  for (let i = 1; i < polygon.length; i += 1) {
    if (ringContainsLngLat(polygon[i], lng, lat)) return false;
  }
  return true;
}

/** Polygon / MultiPolygon en lng-lat. Los tiles recortan la provincia al tile: el recorte igual contiene el centro. */
export function geometryContainsLngLat(geometry, lngLat) {
  if (!geometry || !lngLat) return false;
  const { lng, lat } = lngLat;
  if (!Number.isFinite(lng) || !Number.isFinite(lat)) return false;
  if (geometry.type === 'Polygon') return polygonContainsLngLat(geometry.coordinates, lng, lat);
  if (geometry.type === 'MultiPolygon') {
    return geometry.coordinates.some((polygon) => polygonContainsLngLat(polygon, lng, lat));
  }
  return false;
}

/**
 * Provincia bajo el centro. Por encima del último zoom con datos,
 * `provinces-admin` (maxzoom 10 fijo) llega vacía: ahí se lee `provinces-lookup`,
 * que estira el último tile real.
 *
 * @param {import('maplibre-gl').Map} map
 * @returns {string|null}
 */
export function provinceSlugAtCenter(map) {
  if (!map || typeof map.getCenter !== 'function') return null;
  const center = map.getCenter();
  if (typeof map.project === 'function' && typeof map.getLayer === 'function') {
    const rendered = queryProvinceFeatureAtPoint(map, map.project(center));
    const fromRendered = getProvinceSlugFromProps(rendered?.properties);
    if (fromRendered) return fromRendered;
  }
  if (typeof map.getSource !== 'function' || typeof map.querySourceFeatures !== 'function') return null;
  for (const sourceId of ['provinces-admin', PROVINCES_LOOKUP_SOURCE_ID]) {
    if (!map.getSource(sourceId)) continue;
    let features = [];
    try {
      features = map.querySourceFeatures(sourceId, { sourceLayer: 'provinces' }) || [];
    } catch {
      features = [];
    }
    const hit = features.find((feature) => geometryContainsLngLat(feature?.geometry, center));
    const slug = getProvinceSlugFromProps(hit?.properties);
    if (slug) return slug;
  }
  return null;
}

function normalizeName(name) {
  return String(name || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .trim();
}

function getProvinceNameFromProps(props) {
  if (!props || typeof props !== 'object') return '';
  const keys = ['province_name', 'name', 'NOMPROV', 'provincia', 'PROVINCIA', 'NAME_1', 'Name'];
  for (const k of keys) {
    if (typeof props[k] === 'string' && props[k].trim() !== '') return props[k].trim();
  }
  return '';
}

function getProvinceSlugFromProps(props) {
  const normalized = normalizeName(getProvinceNameFromProps(props));
  if (!normalized) return null;
  const explicit = {
    'c.a.b.a.': 'caba',
    caba: 'caba',
  };
  if (explicit[normalized]) return explicit[normalized];
  return normalized.replace(/\s+/g, '_');
}

function buildDepartmentsOriginalUrlForProvinceSlug(slug) {
  if (!slug) return null;
  return `${DEPARTMENTS_URL_PREFIX}/${slug}/departments/${slug}_departments.pmtiles`;
}

function buildMunicipalitiesOriginalUrlForProvinceSlug(slug) {
  if (!slug) return null;
  return `${DEPARTMENTS_URL_PREFIX}/${slug}/municipalities/${slug}_municipalities.pmtiles`;
}

function buildPedaniasOriginalUrlForProvinceSlug(slug) {
  if (!slug) return null;
  return `${DEPARTMENTS_URL_PREFIX}/${slug}/pedanias/${slug}_pedanias.pmtiles`;
}

function buildNeighborhoodsOriginalUrlForProvinceSlug(slug) {
  if (!slug) return null;
  const neighborhoodsSlug = slug === 'ciudad_autonoma_de_buenos_aires'
    ? 'caba'
    : slug;
  return `${DEPARTMENTS_URL_PREFIX}/${neighborhoodsSlug}/neighborhoods/${neighborhoodsSlug}_neighborhoods.pmtiles`;
}

function isCabaProvinceSlug(slug) {
  return slug === 'caba' || slug === 'ciudad_autonoma_de_buenos_aires';
}

function provinceSupportsAdministrativeLayer(slug, layerKey) {
  if (!slug || !layerKey) return false;
  const defaultSupportByLayer = {
    municipalities: true,
    pedanias: false,
    neighborhoods: true,
  };
  const capabilities = PROVINCE_ADMIN_LAYER_CAPABILITIES[slug];
  if (!capabilities || !Object.prototype.hasOwnProperty.call(capabilities, layerKey)) {
    return Boolean(defaultSupportByLayer[layerKey]);
  }
  return Boolean(capabilities[layerKey]);
}

function getDepartmentIdentityForFilter(props) {
  if (!props || typeof props !== 'object') return null;
  const keys = ['department_id', 'depto_id', 'dept_id', 'id', 'department_name', 'name'];
  for (const k of keys) {
    if (!Object.prototype.hasOwnProperty.call(props, k)) continue;
    const v = props[k];
    if (typeof v === 'string' && v.trim() !== '') return { key: k, value: v.trim() };
    if (typeof v === 'number' && !Number.isNaN(v)) return { key: k, value: v };
  }
  return null;
}

function buildDepartmentFilter(identity) {
  if (!identity) return FILTER_NEVER_MATCH;
  return ['==', ['get', identity.key], identity.value];
}

function buildDepartmentSourceFilter(identity, { lenientTypes = false } = {}) {
  if (!identity) return FILTER_NEVER_MATCH;
  if (!lenientTypes) return buildDepartmentFilter(identity);

  // En algunos tiles (especialmente CABA) el mismo ID puede venir como número o string.
  // Para construir bounds completos, aceptamos ambas comparaciones.
  const asString = String(identity.value);
  const asNumber = Number(identity.value);

  if (!Number.isNaN(asNumber)) {
    return [
      'any',
      ['==', ['get', identity.key], identity.value],
      ['==', ['to-string', ['get', identity.key]], asString],
      ['==', ['to-number', ['get', identity.key]], asNumber],
    ];
  }

  return [
    'any',
    ['==', ['get', identity.key], identity.value],
    ['==', ['to-string', ['get', identity.key]], asString],
  ];
}

function getMunicipalityIdentityForFilter(props) {
  if (!props || typeof props !== 'object') return null;
  const keys = ['municipality_id', 'muni_id', 'municipio_id', 'id', 'municipality_name', 'municipio', 'name'];
  for (const k of keys) {
    if (!Object.prototype.hasOwnProperty.call(props, k)) continue;
    const v = props[k];
    if (typeof v === 'string' && v.trim() !== '') return { key: k, value: v.trim() };
    if (typeof v === 'number' && !Number.isNaN(v)) return { key: k, value: v };
  }
  return null;
}

function buildMunicipalityFilter(identity) {
  if (!identity) return FILTER_NEVER_MATCH;
  return ['==', ['get', identity.key], identity.value];
}

function buildMunicipalitySourceFilter(identity, { lenientTypes = false } = {}) {
  if (!identity) return FILTER_NEVER_MATCH;
  if (!lenientTypes) return buildMunicipalityFilter(identity);

  const asString = String(identity.value);
  const asNumber = Number(identity.value);

  if (!Number.isNaN(asNumber)) {
    return [
      'any',
      ['==', ['get', identity.key], identity.value],
      ['==', ['to-string', ['get', identity.key]], asString],
      ['==', ['to-number', ['get', identity.key]], asNumber],
    ];
  }

  return [
    'any',
    ['==', ['get', identity.key], identity.value],
    ['==', ['to-string', ['get', identity.key]], asString],
  ];
}

function buildMunicipalityDepartmentScopeFilter(departmentIdentity) {
  if (!departmentIdentity) return FILTER_NEVER_MATCH;

  const departmentLinkKeys = [
    'department_id',
    'depto_id',
    'dept_id',
    'parent_department_id',
    'department_name',
    'department',
  ];

  const value = departmentIdentity.value;
  const asString = String(value);
  const asNumber = Number(value);
  const clauses = [];

  departmentLinkKeys.forEach((key) => {
    clauses.push(['==', ['get', key], value]);
    clauses.push(['==', ['to-string', ['get', key]], asString]);
    if (!Number.isNaN(asNumber)) {
      clauses.push(['==', ['to-number', ['get', key]], asNumber]);
    }
  });

  return ['any', ...clauses];
}

function getPedaniaIdentityForFilter(props) {
  if (!props || typeof props !== 'object') return null;
  const keys = ['pedania_id', 'pedanía_id', 'id', 'pedania_name', 'pedania', 'name'];
  for (const k of keys) {
    if (!Object.prototype.hasOwnProperty.call(props, k)) continue;
    const v = props[k];
    if (typeof v === 'string' && v.trim() !== '') return { key: k, value: v.trim() };
    if (typeof v === 'number' && !Number.isNaN(v)) return { key: k, value: v };
  }
  return null;
}

function buildPedaniaFilter(identity) {
  if (!identity) return FILTER_NEVER_MATCH;
  return ['==', ['get', identity.key], identity.value];
}

function buildPedaniaScopeFilter(departmentIdentity) {
  if (!departmentIdentity) return FILTER_NEVER_MATCH;
  const departmentLinkKeys = [
    'department_id',
    'depto_id',
    'dept_id',
    'parent_department_id',
    'department_name',
    'department',
    'depto',
    'name',
  ];
  const value = departmentIdentity.value;
  const asString = String(value);
  const asNumber = Number(value);
  const clauses = [];

  // Intenta matchear también contra la misma key usada al seleccionar el departamento.
  departmentLinkKeys.push(departmentIdentity.key);

  departmentLinkKeys.forEach((key) => {
    clauses.push(['==', ['get', key], value]);
    clauses.push(['==', ['to-string', ['get', key]], asString]);
    if (!Number.isNaN(asNumber)) clauses.push(['==', ['to-number', ['get', key]], asNumber]);
  });
  return ['any', ...clauses];
}

function buildMunicipalityPedaniaScopeFilter(pedaniaIdentity, pedaniaProps = null) {
  if (!pedaniaIdentity) return FILTER_NEVER_MATCH;
  const pedaniaLinkKeys = [
    'pedania_id',
    'pedanía_id',
    'pedaniaid',
    'ped_id',
    'parent_pedania_id',
    'parent_pedania',
    'pedania_name',
    'pedania',
    'pedania_nombre',
    'nombre_pedania',
    'pedania_code',
    'codigo_pedania',
    'cod_pedania',
    'name',
    'parent_pedania_name',
    'parent_pedania',
  ];
  const values = new Set();
  values.add(pedaniaIdentity.value);

  if (pedaniaProps && typeof pedaniaProps === 'object') {
    const valueKeys = ['pedania_id', 'pedanía_id', 'id', 'pedania_name', 'pedania', 'name'];
    valueKeys.forEach((key) => {
      const v = pedaniaProps[key];
      if (typeof v === 'string' && v.trim() !== '') values.add(v.trim());
      if (typeof v === 'number' && !Number.isNaN(v)) values.add(v);
    });
  }

  pedaniaLinkKeys.push(pedaniaIdentity.key);
  const clauses = [];

  values.forEach((value) => {
    const asString = String(value);
    const asLowerString = asString.toLowerCase();
    const asNumber = Number(value);
    pedaniaLinkKeys.forEach((key) => {
      clauses.push(['==', ['get', key], value]);
      clauses.push(['==', ['to-string', ['get', key]], asString]);
      clauses.push(['==', ['downcase', ['to-string', ['get', key]]], asLowerString]);
      if (!Number.isNaN(asNumber)) clauses.push(['==', ['to-number', ['get', key]], asNumber]);
    });
  });

  return clauses.length > 0 ? ['any', ...clauses] : FILTER_NEVER_MATCH;
}

function polygonContainsPoint(ring, point) {
  const x = point[0];
  const y = point[1];
  let inside = false;
  for (let i = 0, j = ring.length - 1; i < ring.length; j = i++) {
    const xi = ring[i][0];
    const yi = ring[i][1];
    const xj = ring[j][0];
    const yj = ring[j][1];
    const intersects = ((yi > y) !== (yj > y))
      && (x < (((xj - xi) * (y - yi)) / ((yj - yi) || 1e-12)) + xi);
    if (intersects) inside = !inside;
  }
  return inside;
}

function polygonGeometryContainsPoint(geometry, point) {
  if (!geometry || !point) return false;
  if (geometry.type === 'Polygon') {
    const rings = geometry.coordinates || [];
    if (!rings.length) return false;
    if (!polygonContainsPoint(rings[0], point)) return false;
    for (let i = 1; i < rings.length; i += 1) {
      if (polygonContainsPoint(rings[i], point)) return false;
    }
    return true;
  }
  if (geometry.type === 'MultiPolygon') {
    return (geometry.coordinates || []).some((polygon) => {
      if (!polygon.length) return false;
      if (!polygonContainsPoint(polygon[0], point)) return false;
      for (let i = 1; i < polygon.length; i += 1) {
        if (polygonContainsPoint(polygon[i], point)) return false;
      }
      return true;
    });
  }
  return false;
}

function geometryCentroid(geometry) {
  if (!geometry) return null;
  const bounds = boundsFromFeatureGeometry(geometry);
  if (!bounds || bounds.isEmpty()) return null;
  const c = bounds.getCenter();
  return [c.lng, c.lat];
}

function polygonRingCentroid(ring) {
  if (!Array.isArray(ring) || ring.length < 3) return null;
  let twiceArea = 0;
  let cxTimes6Area = 0;
  let cyTimes6Area = 0;

  for (let i = 0; i < ring.length - 1; i += 1) {
    const p1 = ring[i];
    const p2 = ring[i + 1];
    if (
      !Array.isArray(p1) || p1.length < 2 || typeof p1[0] !== 'number' || typeof p1[1] !== 'number'
      || !Array.isArray(p2) || p2.length < 2 || typeof p2[0] !== 'number' || typeof p2[1] !== 'number'
    ) {
      continue;
    }
    const cross = (p1[0] * p2[1]) - (p2[0] * p1[1]);
    twiceArea += cross;
    cxTimes6Area += (p1[0] + p2[0]) * cross;
    cyTimes6Area += (p1[1] + p2[1]) * cross;
  }

  if (Math.abs(twiceArea) < 1e-12) return null;
  return [cxTimes6Area / (3 * twiceArea), cyTimes6Area / (3 * twiceArea)];
}

function geometryLabelPoint(geometry) {
  if (!geometry) return null;

  if (geometry.type === 'Polygon') {
    const outerRing = Array.isArray(geometry.coordinates) ? geometry.coordinates[0] : null;
    return polygonRingCentroid(outerRing) || geometryCentroid(geometry);
  }

  if (geometry.type === 'MultiPolygon') {
    const polygons = Array.isArray(geometry.coordinates) ? geometry.coordinates : [];
    let bestRing = null;
    let bestAreaAbs = -1;

    polygons.forEach((polygon) => {
      const ring = Array.isArray(polygon) ? polygon[0] : null;
      if (!Array.isArray(ring) || ring.length < 3) return;
      let ringTwiceArea = 0;
      for (let i = 0; i < ring.length - 1; i += 1) {
        const p1 = ring[i];
        const p2 = ring[i + 1];
        if (
          !Array.isArray(p1) || p1.length < 2 || typeof p1[0] !== 'number' || typeof p1[1] !== 'number'
          || !Array.isArray(p2) || p2.length < 2 || typeof p2[0] !== 'number' || typeof p2[1] !== 'number'
        ) {
          continue;
        }
        ringTwiceArea += (p1[0] * p2[1]) - (p2[0] * p1[1]);
      }
      const areaAbs = Math.abs(ringTwiceArea);
      if (areaAbs > bestAreaAbs) {
        bestAreaAbs = areaAbs;
        bestRing = ring;
      }
    });

    return polygonRingCentroid(bestRing) || geometryCentroid(geometry);
  }

  return geometryCentroid(geometry);
}

function getNeighborhoodNameFromProps(props) {
  if (!props || typeof props !== 'object') return null;
  const candidate =
    (typeof props.neighborhood_name === 'string' && props.neighborhood_name.trim())
    || (typeof props.barrio === 'string' && props.barrio.trim())
    || (typeof props.name === 'string' && props.name.trim())
    || null;
  return candidate;
}

function buildMunicipalitySpatialScopeFilterFromPedaniaFeatures(municipalityFeatures, pedaniaFeatures) {
  if (!Array.isArray(municipalityFeatures) || !Array.isArray(pedaniaFeatures)) return FILTER_NEVER_MATCH;
  if (!municipalityFeatures.length || !pedaniaFeatures.length) return FILTER_NEVER_MATCH;

  const matchedFilters = [];
  municipalityFeatures.forEach((municipalityFeature) => {
    const centroid = geometryCentroid(municipalityFeature.geometry);
    if (!centroid) return;
    const isInsideAnyPedania = pedaniaFeatures.some((pedaniaFeature) =>
      polygonGeometryContainsPoint(pedaniaFeature.geometry, centroid));
    if (!isInsideAnyPedania) return;
    const municipalityIdentity = getMunicipalityIdentityForFilter(municipalityFeature.properties);
    if (!municipalityIdentity) return;
    matchedFilters.push(buildMunicipalityFilter(municipalityIdentity));
  });

  if (!matchedFilters.length) return FILTER_NEVER_MATCH;
  return ['any', ...matchedFilters];
}

function moveDepartmentLayersToTop(map) {
  if (!canAccessMapStyle(map)) return;
  [ACTIVE_DEPARTMENT_HIT_LAYER_ID, ACTIVE_DEPARTMENT_BORDERS_LAYER_ID, ACTIVE_DEPARTMENT_HOVER_FILL_LAYER_ID, ACTIVE_DEPARTMENT_HOVER_LAYER_ID, ACTIVE_DEPARTMENT_SELECTED_LAYER_ID]
    .forEach((id) => {
      if (map.getLayer(id)) map.moveLayer(id);
    });
}

function moveMunicipalityLayersToTop(map) {
  if (!canAccessMapStyle(map)) return;
  [ACTIVE_MUNICIPALITY_HIT_LAYER_ID, ACTIVE_MUNICIPALITY_BORDERS_LAYER_ID, ACTIVE_MUNICIPALITY_HOVER_FILL_LAYER_ID, ACTIVE_MUNICIPALITY_HOVER_LAYER_ID, ACTIVE_MUNICIPALITY_SELECTED_LAYER_ID]
    .forEach((id) => {
      if (map.getLayer(id)) map.moveLayer(id);
    });
}

function movePedaniaLayersToTop(map) {
  if (!canAccessMapStyle(map)) return;
  [ACTIVE_PEDANIA_HIT_LAYER_ID, ACTIVE_PEDANIA_BORDERS_LAYER_ID, ACTIVE_PEDANIA_HOVER_FILL_LAYER_ID, ACTIVE_PEDANIA_HOVER_LAYER_ID, ACTIVE_PEDANIA_SELECTED_LAYER_ID]
    .forEach((id) => {
      if (map.getLayer(id)) map.moveLayer(id);
    });
}

function moveNeighborhoodLayersToTop(map) {
  if (!canAccessMapStyle(map)) return;
  [
    ACTIVE_NEIGHBORHOOD_HIT_LAYER_ID,
    ACTIVE_NEIGHBORHOOD_BORDERS_LAYER_ID,
    ACTIVE_NEIGHBORHOOD_LABELS_LAYER_ID,
  ].forEach((id) => {
    if (map.getLayer(id)) map.moveLayer(id);
  });
}

function moveProvinceLabelsToTop(map) {
  if (!canAccessMapStyle(map)) return;
  if (map.getLayer('provinces-labels')) map.moveLayer('provinces-labels');
}

function moveProvinceSelectedBorderToTop(map) {
  if (!canAccessMapStyle(map)) return;
  if (map.getLayer('provinces-admin-hover-borders')) map.moveLayer('provinces-admin-hover-borders');
  if (map.getLayer('provinces-labels')) map.moveLayer('provinces-labels');
}

function movePedaniaSelectedBorderToTop(map) {
  if (!canAccessMapStyle(map)) return;
  if (map.getLayer(ACTIVE_PEDANIA_SELECTED_LAYER_ID)) map.moveLayer(ACTIVE_PEDANIA_SELECTED_LAYER_ID);
  if (map.getLayer('provinces-labels')) map.moveLayer('provinces-labels');
}

function getAdministrativeBordersColorForView(viewId) {
  return viewId === 'street'
    ? MAP_BASE_STYLE.administrativeBordersColorLightMap
    : MAP_BASE_STYLE.administrativeBordersColorDarkMap;
}

function getNeighborhoodBordersColorForView(viewId) {
  return viewId === 'street'
    ? (MAP_BASE_STYLE.neighborhoodBorderColorStreet ?? '#000000')
    : (MAP_BASE_STYLE.neighborhoodBorderColor ?? '#ffffff');
}

/** Calle IGN (claro u oscuro): el raster ya aporta contexto — sin etiquetas de provincia propias. */
function isStreetRasterView(viewId) {
  return viewId === 'street' || viewId === 'streetDark';
}

/**
 * Inicializa y mantiene la instancia del mapa MapLibre.
 *
 * @param {object} options
 * @param {Function} options.onLoad          — callback cuando el mapa termina de cargar
 * @param {Function} options.onMove          — callback en move/zoom (para PMTiles dinámicos)
 * @param {Function} options.onMapClick      — callback en click general del mapa
 * @param {Function} options.onLocationError — callback cuando falla geolocalización
 */

// BBOX de Argentina [SW, NE] — restringir zoom al territorio nacional
const ARGENTINA_BOUNDS = [
  [-118, -57],
  [-10, -16],
];

// ─── Tile providers ───────────────────────────────────────────────────────────
const MAPBOX_ACCESS_TOKEN = process.env.REACT_APP_MAPBOX_ACCESS_TOKEN || '';
/** Si hay token, satellite1 usa raster Mapbox (mejor calidad, p. ej. Argentina); si no, ArcGIS World Imagery. */
const SATELLITE1_USE_MAPBOX = Boolean(MAPBOX_ACCESS_TOKEN);
const SATELLITE1_MAPBOX_TILES_URL = `https://api.mapbox.com/v4/mapbox.satellite/{z}/{x}/{y}@2x.jpg90?access_token=${MAPBOX_ACCESS_TOKEN}`;
const SATELLITE1_ARCGIS_TILES_URL =
  'https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}';
const SATELLITE1_TILES_URL = SATELLITE1_USE_MAPBOX ? SATELLITE1_MAPBOX_TILES_URL : SATELLITE1_ARCGIS_TILES_URL;
const SATELLITE1_TILE_SIZE = SATELLITE1_USE_MAPBOX ? 512 : 256;
const SATELLITE1_ATTRIBUTION = SATELLITE1_USE_MAPBOX ? '© Mapbox © OpenStreetMap' : undefined;
// satellite2 — ArcGIS Clarity (mayor resolución en zoom alto) // Comentado temporalmente, baja resolución. 
// const SATELLITE2_TILES_URL = 'https://clarity.maptiles.arcgis.com/arcgis/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}';
const SATELLITE2_TILES_URL = 'https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}';
// street — IGN Argenmap (oficial, protocolo TMS).
// No van en el estilo inicial: opacity 0 igual dispara fetch y, si IGN está caído,
// MapLibre no dispara `load` → no se firman provincias/parcelas. Se montan al elegir calle.
const STREET_TILES_URL = 'https://wms.ign.gob.ar/geoserver/gwc/service/tms/1.0.0/capabaseargenmap@EPSG%3A3857@png/{z}/{x}/{y}.png';
const STREET_TILES_URL_DARK_NO_LABELS =
  'https://wms.ign.gob.ar/geoserver/gwc/service/tms/1.0.0/argenmap_oscuro@EPSG%3A3857@png/{z}/{x}/{y}.png';
const IGN_RASTER_BEFORE_LAYER_ID = 'overlay-roads-major';
const IGN_RASTER_VIEWS = {
  street: {
    sourceId: 'street',
    layerId: 'street-layer',
    tilesUrl: STREET_TILES_URL,
  },
  streetDark: {
    sourceId: 'streetDark',
    layerId: 'street-dark-layer',
    tilesUrl: STREET_TILES_URL_DARK_NO_LABELS,
  },
};

function ensureIgnStreetRaster(map, viewId) {
  const spec = IGN_RASTER_VIEWS[viewId];
  if (!map || !spec) return;
  if (!map.getSource(spec.sourceId)) {
    map.addSource(spec.sourceId, {
      type: 'raster',
      tiles: [spec.tilesUrl],
      scheme: 'tms',
      tileSize: 256,
      minzoom: 3,
      maxzoom: 18,
    });
  }
  if (!map.getLayer(spec.layerId)) {
    const beforeId = map.getLayer(IGN_RASTER_BEFORE_LAYER_ID)
      ? IGN_RASTER_BEFORE_LAYER_ID
      : undefined;
    map.addLayer({
      id: spec.layerId,
      type: 'raster',
      source: spec.sourceId,
      minzoom: 0,
      maxzoom: 20,
      layout: { visibility: 'visible' },
      paint: { 'raster-opacity': 1 },
    }, beforeId);
    return;
  }
  map.setLayoutProperty(spec.layerId, 'visibility', 'visible');
  map.setPaintProperty(spec.layerId, 'raster-opacity', 1);
}

function hideIgnStreetRasters(map) {
  if (!map) return;
  Object.values(IGN_RASTER_VIEWS).forEach(({ layerId }) => {
    if (!map.getLayer(layerId)) return;
    map.setLayoutProperty(layerId, 'visibility', 'none');
    map.setPaintProperty(layerId, 'raster-opacity', 0);
  });
}

export function useMapInstance({
  onLoad,
  onMove,
  onMapClick,
  onLocationError,
  onAdministrativeLoadChange,
  onProvinceSelectionChange,
  onDepartmentSelectionChange,
  onPedaniaSelectionChange,
  onMunicipalitySelectionChange,
  onNeighborhoodHoverChange,
  autoCenterOnLoad = true,
  // Quién monta el mapa, para `metadata.surface` del reporte de WebGL ('app', 'shared_list', …).
  surface = null,
}) {
  const mapRef         = useRef(null);
  const mapInstanceRef = useRef(null);
  // Sin WebGL no hay mapa: las páginas muestran `MapUnavailable` y todo lo demás sigue.
  const [mapUnavailableReason, setMapUnavailableReason] = useState(() => (
    isWebGLAvailable() ? null : (getWebGLSupport().reason || 'unsupported')
  ));
  const mapBaseViewIdRef = useRef('satellite1');
  const onProvinceSelectionChangeRef = useRef(onProvinceSelectionChange);
  const onDepartmentSelectionChangeRef = useRef(onDepartmentSelectionChange);
  const onPedaniaSelectionChangeRef = useRef(onPedaniaSelectionChange);
  const onMunicipalitySelectionChangeRef = useRef(onMunicipalitySelectionChange);
  const onNeighborhoodHoverChangeRef = useRef(onNeighborhoodHoverChange);
  const onAdministrativeLoadChangeRef = useRef(onAdministrativeLoadChange);
  const clearAdministrativeSelectionRef = useRef(() => {});
  const resetAdministrativeViewRef = useRef(() => {});
  const focusAdministrativeLevelRef = useRef(() => false);

  onProvinceSelectionChangeRef.current = onProvinceSelectionChange;
  onDepartmentSelectionChangeRef.current = onDepartmentSelectionChange;
  onPedaniaSelectionChangeRef.current = onPedaniaSelectionChange;
  onMunicipalitySelectionChangeRef.current = onMunicipalitySelectionChange;
  onNeighborhoodHoverChangeRef.current = onNeighborhoodHoverChange;
  onAdministrativeLoadChangeRef.current = onAdministrativeLoadChange;

  const getUserLocation = () =>
    new Promise((resolve, reject) => {
      if (!navigator.geolocation) {
        reject(new Error('Tu navegador no soporta geolocalizacion.'));
        return;
      }

      navigator.geolocation.getCurrentPosition(
        ({ coords }) => resolve({ lng: coords.longitude, lat: coords.latitude }),
        (error) => {
          if (error.code === error.PERMISSION_DENIED) {
            reject(new Error('Permiso de ubicacion denegado.'));
            return;
          }

          if (error.code === error.TIMEOUT) {
            reject(new Error('No se pudo obtener tu ubicacion a tiempo.'));
            return;
          }

          reject(new Error('No se pudo obtener tu ubicacion actual.'));
        },
        {
          enableHighAccuracy: true,
          timeout: 10000,
          maximumAge: 60000,
        }
      );
    });

  const centerOnUserLocation = async ({ silent = false } = {}) => {
    try {
      const location = await getUserLocation();
      const map = mapInstanceRef.current;

      if (!map) {
        return { success: false };
      }

      map.easeTo({
        center: [location.lng, location.lat],
        zoom: Math.max(map.getZoom(), 15),
        duration: 1200,
      });

      return { success: true, location };
    } catch (error) {
      if (onLocationError) {
        onLocationError(error, { silent });
      } else if (!silent) {
        console.warn(error.message);
      }

      return { success: false, error };
    }
  };

  useEffect(() => {
    if (mapInstanceRef.current) return;
    if (!isWebGLAvailable()) {
      // Sin WebGL MapLibre tira en el constructor: no se crea el mapa y la página sigue.
      const support = getWebGLSupport();
      setMapUnavailableReason(support.reason || 'unsupported');
      reportWebGLUnavailableOnce({ support, surface, embedded: isEmbeddedFrame() });
      return undefined;
    }

    let cancelled = false;
    let visibilityHandler = null;
    let provincesPmtilesRefreshDebounced = null;
    let neighborhoodTilesSourcedataHandler = null;
    let hoveredProvinceName = null;
    let hoveredDepartmentKey = null;
    let hoveredPedaniaKey = null;
    let hoveredMunicipalityKey = null;
    let hoveredNeighborhoodName = null;
    let hoveredPedaniaFilter = FILTER_NEVER_MATCH;
    let hoveredMunicipalityFilter = FILTER_NEVER_MATCH;
    let selectedProvinceFilter = PROVINCE_HOVER_INACTIVE;
    let selectedDepartmentFilter = FILTER_NEVER_MATCH;
    let selectedPedaniaFilter = FILTER_NEVER_MATCH;
    let selectedMunicipalityFilter = FILTER_NEVER_MATCH;
    let pedaniaDepartmentScopeFilter = FILTER_NEVER_MATCH;
    let municipalityDepartmentScopeFilter = FILTER_NEVER_MATCH;
    let municipalityParentScopeFilter = FILTER_NEVER_MATCH;
    let activeDepartmentsSourceLayer = null;
    let activeDepartmentsOriginalUrl = null;
    let activeDepartmentsSlug = null;
    let activePedaniasSourceLayer = null;
    let activePedaniasOriginalUrl = null;
    let activePedaniasSlug = null;
    let activeMunicipalitiesSourceLayer = null;
    let activeMunicipalitiesOriginalUrl = null;
    let activeMunicipalitiesSlug = null;
    let activeNeighborhoodsSourceLayer = null;
    let activeNeighborhoodsOriginalUrl = null;
    let activeNeighborhoodsSlug = null;
    let departmentTooltipPopup = null;
    let ensureBaDepartmentLayers = async () => false;
    let removeBaDepartmentLayersAndSource = () => {};
    let getDepartmentBounds = () => null;
    let ensureMunicipalityLayers = async () => false;
    let ensureNeighborhoodLayers = async () => false;
    let syncNeighborhoodLabelsSource = () => {};
    let ensurePedaniaLayers = async () => false;
    let removePedaniaLayersAndSource = () => {};
    let removeNeighborhoodLayersAndSource = () => {};
    let getPedaniaBounds = () => null;
    let removeMunicipalityLayersAndSource = () => {};
    let getMunicipalityBounds = () => null;
    let applyPedaniaLayerFilters = () => {};
    let applyMunicipalityLayerFilters = () => {};
    let provinceHoverRaf = null;
    let handleProvincePointerMove = null;
    let handlePointerLeaveCanvas = null;
    let clearProvinceSelection = () => {};
    let selectedProvinceSnapshot = null;
    let selectedDepartmentSnapshot = null;
    let selectedPedaniaSnapshot = null;
    let selectedMunicipalitySnapshot = null;
    let administrativeLoadListenersAttached = false;
    let administrativeLoadSettled = false;
    let administrativeLoadFallbackTimeout = null;
    let handleAdministrativeLoadProgress = () => {};

    const emitAdministrativeLoadState = (isLoading) => {
      if (!onAdministrativeLoadChangeRef.current) return;
      onAdministrativeLoadChangeRef.current(isLoading);
    };

    const mapOptions = {
      container: mapRef.current,
      style: {
        version: 8,
        glyphs: 'https://fonts.openmaptiles.org/{fontstack}/{range}.pbf',
        sources: {
          satellite1: {
            type: 'raster',
            tiles: [SATELLITE1_TILES_URL],
            tileSize: SATELLITE1_TILE_SIZE,
            ...(SATELLITE1_ATTRIBUTION ? { attribution: SATELLITE1_ATTRIBUTION } : {}),
          },
          satellite2: {
            type: 'raster',
            tiles: [SATELLITE2_TILES_URL],
            tileSize: 256,
            maxzoom: 17,
          },
          satelliteLabels: {
            type: 'raster',
            tiles: ['https://server.arcgisonline.com/ArcGIS/rest/services/Reference/World_Transportation/MapServer/tile/{z}/{y}/{x}'],
            tileSize: 256,
          },
          'overlay-labels': {
            type: 'vector',
            url: 'https://tiles.openfreemap.org/planet', // gratuito, sin key
          },
        },
        layers: [
          {
            id: 'background',
            type: 'background',
            paint: { 'background-color': MAP_BASE_STYLE.backgroundColor },
          },
          // Las tres capas raster siempre presentes — la vista activa tiene opacity 1,
          // las inactivas tienen opacity 0. Así el cambio es instantáneo sin recargar tiles.
          {
            id: 'satellite1-layer',
            type: 'raster',
            source: 'satellite1',
            minzoom: 0,
            maxzoom: 20,
            paint: { 'raster-opacity': 1 }, // vista por defecto
          },
          {
            id: 'satellite2-layer',
            type: 'raster',
            source: 'satellite2',
            minzoom: 0,
            maxzoom: 20,
            paint: { 'raster-opacity': 0 },
          },
          {
            id: 'satellite-labels-layer',
            type: 'raster',
            source: 'satelliteLabels',
            minzoom: 15,
            maxzoom: 20,
            paint: { 'raster-opacity': 0.8, },
          },
          // Overlay vectorial desde OpenFreeMap:
          // - overlay-roads-major: rutas nacionales/provinciales principales (motorway, trunk, primary, secondary)
          // - overlay-roads-minor: rutas secundarias y caminos de servicio
          // - overlay-streets: calles urbanas finas en zoom alto
          // - overlay-place-labels: nombres de ciudades/localidades
          // - overlay-road-labels: número/ref de ruta
          // - overlay-street-labels: nombres de calles
          {
            id: 'overlay-roads-major',
            type: 'line',
            source: 'overlay-labels',
            'source-layer': 'transportation',
            filter: ['in', 'class', 'motorway', 'trunk', 'primary', 'secondary'],
            minzoom: 6,
            paint: {
              'line-color': [
                'interpolate', ['linear'], ['zoom'],
                5, 'rgba(255,255,255,0.3)',
                10, 'rgba(255,255,255,0.6)',
              ],
              'line-width': [
                'interpolate', ['linear'], ['zoom'],
                5, 0.5,
                10, 1.2,
                14, 2,
              ],
            },
            layout: { visibility: 'visible' },
          },
          {
            id: 'overlay-roads-minor',
            type: 'line',
            source: 'overlay-labels',
            'source-layer': 'transportation',
            filter: ['in', 'class', 'tertiary', 'minor', 'service'],
            minzoom: 10,
            paint: {
              'line-color': 'rgba(255,255,255,0.35)',
              'line-width': [
                'interpolate', ['linear'], ['zoom'],
                10, 0.4,
                14, 2,
              ],
            },
            layout: { visibility: 'visible' },
          },
          {
            id: 'overlay-place-labels',
            type: 'symbol',
            source: 'overlay-labels',
            'source-layer': 'place',
            filter: ['in', 'class', 'city', 'town', 'village', 'hamlet'],
            minzoom: 6,
            layout: {
              'text-field': ['get', 'name:latin'],
              'text-font': ['Open Sans Bold'],
              'text-size': [
                'interpolate', ['linear'], ['zoom'],
                5, 10,
                10, 13,
                14, 15,
              ],
              'text-max-width': 8,
              'text-allow-overlap': false,
              'symbol-sort-key': ['get', 'rank'],
              visibility: 'visible',
            },
            paint: {
              'text-color': '#ffffff',
              'text-halo-color': 'rgba(0,0,0,0.8)',
              'text-halo-width': 2,
              'text-halo-blur': 0.5,
            },
          },
          {
            id: 'overlay-road-labels',
            type: 'symbol',
            source: 'overlay-labels',
            'source-layer': 'transportation_name',
            minzoom: 9,
            layout: {
              'text-field': ['get', 'ref'],
              'text-font': ['Open Sans Bold'],
              'text-size': 10,
              'symbol-placement': 'line',
              visibility: 'visible',
            },
            paint: {
              'text-color': '#ffffff',
              'text-halo-color': 'rgba(0,0,0,0.7)',
              'text-halo-width': 1.5,
            },
          },
          // Nombres de calles (zoom alto)
          {
            id: 'overlay-street-labels',
            type: 'symbol',
            source: 'overlay-labels',
            'source-layer': 'transportation_name',
            filter: ['in', 'class', 'street', 'residential'],
            minzoom: 10,
            layout: {
              'text-field': ['coalesce', ['get', 'name:latin'], ['get', 'name']],
              'text-font': ['Open Sans Regular'],
              'text-size': 10,
              'symbol-placement': 'line',
              'text-max-angle': 30,
              visibility: 'visible',
            },
            paint: {
              'text-color': 'rgba(255,255,255,0.85)',
              'text-halo-color': 'rgba(0,0,0,0.7)',
              'text-halo-width': 1.2,
            },
          },
        ],
      },
      center: INITIAL_MAP_VIEW.center,
      zoom: INITIAL_MAP_VIEW.zoom,
      maxZoom: 18,
      maxBounds: ARGENTINA_BOUNDS,
      fadeDuration: 0,
    };

    try {
      mapInstanceRef.current = new maplibregl.Map(mapOptions);
    } catch (error) {
      // La prueba de WebGL pasó pero el contexto no se pudo crear (driver, límite de contextos).
      mapInstanceRef.current = null;
      cleanupFailedMapContainer(mapOptions.container);
      const support = markWebGLUnavailable(error);
      setMapUnavailableReason(support.reason || 'map_init_failed');
      reportWebGLUnavailableOnce({ support, surface, embedded: isEmbeddedFrame() });
      return undefined;
    }

    mapInstanceRef.current.on('load', () => {
      if (cancelled) return;
      const map = mapInstanceRef.current;
      emitAdministrativeLoadState(true);
      // El banner no puede esperar a IGN ni a una firma colgada: 12s desde `load`.
      administrativeLoadFallbackTimeout = setTimeout(() => {
        if (administrativeLoadSettled) return;
        administrativeLoadSettled = true;
        emitAdministrativeLoadState(false);
      }, 12000);

      map.on('styledata', () => {
        if (!canAccessMapStyle(map)) return;
        moveNeighborhoodLayersToTop(map);
        moveProvinceLayersToTop(map);
        moveMunicipalityLayersToTop(map);
        movePedaniaLayersToTop(map);
        moveDepartmentLayersToTop(map);
        moveProvinceLabelsToTop(map);
        if (selectedProvinceFilter !== PROVINCE_HOVER_INACTIVE) {
          moveProvinceSelectedBorderToTop(map);
        }
        if (selectedPedaniaFilter !== FILTER_NEVER_MATCH) {
          movePedaniaSelectedBorderToTop(map);
        }
        moveBaseLabelsToTop(map);
      });

      // Parcelas / índice PMTiles no dependen de IGN ni de la firma de provincias.
      if (onLoad) onLoad(map);
      if (autoCenterOnLoad) centerOnUserLocation({ silent: true });

      void (async () => {
        const provincesSignedUrl = await getSignedPmtilesUrl(PROVINCES_ORIGINAL_URL);
        if (cancelled || map !== mapInstanceRef.current || !canAccessMapStyle(map)) return;
        const clearDepartmentHover = () => {
          hoveredDepartmentKey = null;
          if (map.getLayer(ACTIVE_DEPARTMENT_HOVER_FILL_LAYER_ID)) {
            map.setFilter(ACTIVE_DEPARTMENT_HOVER_FILL_LAYER_ID, FILTER_NEVER_MATCH);
          }
          if (map.getLayer(ACTIVE_DEPARTMENT_HOVER_LAYER_ID)) {
            map.setFilter(ACTIVE_DEPARTMENT_HOVER_LAYER_ID, FILTER_NEVER_MATCH);
          }
          if (departmentTooltipPopup) {
            departmentTooltipPopup.remove();
            departmentTooltipPopup = null;
          }
        };
        const clearDepartmentSelection = () => {
          selectedDepartmentFilter = FILTER_NEVER_MATCH;
          if (map.getLayer(ACTIVE_DEPARTMENT_SELECTED_LAYER_ID)) {
            map.setFilter(ACTIVE_DEPARTMENT_SELECTED_LAYER_ID, FILTER_NEVER_MATCH);
          }
          if (onDepartmentSelectionChangeRef.current) onDepartmentSelectionChangeRef.current(null);
        };
        const clearPedaniaHover = () => {
          hoveredPedaniaKey = null;
          hoveredPedaniaFilter = FILTER_NEVER_MATCH;
          applyPedaniaLayerFilters();
        };
        const clearPedaniaSelection = () => {
          selectedPedaniaFilter = FILTER_NEVER_MATCH;
          municipalityParentScopeFilter = FILTER_NEVER_MATCH;
          applyPedaniaLayerFilters();
          applyMunicipalityLayerFilters();
          if (onPedaniaSelectionChangeRef.current) onPedaniaSelectionChangeRef.current(null);
        };
        const clearMunicipalityHover = () => {
          hoveredMunicipalityKey = null;
          hoveredMunicipalityFilter = FILTER_NEVER_MATCH;
          applyMunicipalityLayerFilters();
        };
        const clearMunicipalitySelection = () => {
          selectedMunicipalityFilter = FILTER_NEVER_MATCH;
          applyMunicipalityLayerFilters();
          if (onMunicipalitySelectionChangeRef.current) onMunicipalitySelectionChangeRef.current(null);
        };
        const setNeighborhoodHoverName = (nextName) => {
          if (hoveredNeighborhoodName === nextName) return;
          hoveredNeighborhoodName = nextName;
          if (onNeighborhoodHoverChangeRef.current) onNeighborhoodHoverChangeRef.current(nextName);
        };
        const queryDepartmentFeatureAtPoint = (point) => {
          if (!map.getLayer(ACTIVE_DEPARTMENT_HIT_LAYER_ID)) return null;
          const hits = map.queryRenderedFeatures(point, { layers: [ACTIVE_DEPARTMENT_HIT_LAYER_ID] });
          return hits[0] || null;
        };
        const queryMunicipalityFeatureAtPoint = (point) => {
          if (!map.getLayer(ACTIVE_MUNICIPALITY_HIT_LAYER_ID)) return null;
          const hits = map.queryRenderedFeatures(point, { layers: [ACTIVE_MUNICIPALITY_HIT_LAYER_ID] });
          return hits[0] || null;
        };
        const queryPedaniaFeatureAtPoint = (point) => {
          if (!map.getLayer(ACTIVE_PEDANIA_HIT_LAYER_ID)) return null;
          const hits = map.queryRenderedFeatures(point, { layers: [ACTIVE_PEDANIA_HIT_LAYER_ID] });
          return hits[0] || null;
        };
        const queryNeighborhoodFeatureAtPoint = (point) => {
          if (!map.getLayer(ACTIVE_NEIGHBORHOOD_HIT_LAYER_ID)) return null;
          const hits = map.queryRenderedFeatures(point, { layers: [ACTIVE_NEIGHBORHOOD_HIT_LAYER_ID] });
          return hits[0] || null;
        };
        syncNeighborhoodLabelsSource = () => {
          if (NEIGHBORHOOD_NAME_RENDER_MODE !== 'labels') return;
          if (!canAccessMapStyle(map)) return;
          if (!activeNeighborhoodsSourceLayer || !map.getSource(ACTIVE_NEIGHBORHOOD_SOURCE_ID)) return;

          let pieces = [];
          try {
            pieces = map.querySourceFeatures(ACTIVE_NEIGHBORHOOD_SOURCE_ID, {
              sourceLayer: activeNeighborhoodsSourceLayer,
            }) || [];
          } catch {
            pieces = [];
          }

          // La capa de etiquetas se monta apuntando a esta fuente: tiene que
          // existir aunque los tiles todavía no hayan llegado.
          if (!map.getSource(ACTIVE_NEIGHBORHOOD_LABELS_SOURCE_ID)) {
            map.addSource(ACTIVE_NEIGHBORHOOD_LABELS_SOURCE_ID, {
              type: 'geojson',
              data: { type: 'FeatureCollection', features: [] },
            });
          }

          // Un encuadre rápido dispara esto antes de que lleguen los tiles:
          // no pisar las etiquetas con un vacío mientras cargan.
          let sourceLoaded = false;
          try {
            sourceLoaded = map.isSourceLoaded(ACTIVE_NEIGHBORHOOD_SOURCE_ID);
          } catch {
            sourceLoaded = false;
          }
          if (pieces.length === 0 && !sourceLoaded) return;

          const byName = new Map();
          pieces.forEach((feature) => {
            const name = getNeighborhoodNameFromProps(feature?.properties);
            if (!name) return;
            const centroid = geometryLabelPoint(feature.geometry);
            if (!centroid) return;
            const bounds = boundsFromFeatureGeometry(feature.geometry);
            if (!bounds || bounds.isEmpty()) return;
            const sw = bounds.getSouthWest();
            const ne = bounds.getNorthEast();
            const weight = Math.abs((ne.lng - sw.lng) * (ne.lat - sw.lat));
            const key = normalizeName(name);
            const prev = byName.get(key);
            if (!prev || weight > prev.weight) {
              byName.set(key, { name, centroid, weight });
            }
          });

          const data = {
            type: 'FeatureCollection',
            features: Array.from(byName.values()).map((item) => ({
              type: 'Feature',
              geometry: { type: 'Point', coordinates: item.centroid },
              properties: { name: item.name },
            })),
          };

          const src = map.getSource(ACTIVE_NEIGHBORHOOD_LABELS_SOURCE_ID);
          if (src && typeof src.setData === 'function') src.setData(data);
        };
        getDepartmentBounds = (feature, identity) => {
          // Para CABA priorizamos la geometría clickeada: evita bounds sobredimensionados
          // al combinar piezas y permite un zoom mucho más cercano por comuna.
          if (activeDepartmentsSlug === 'caba') {
            return boundsFromFeatureGeometry(feature?.geometry);
          }
          // Intentar bounds completos juntando fragmentos del source (más robusto que solo la geometría clickeada).
          if (activeDepartmentsSourceLayer && identity) {
            try {
              const sourceFilter = buildDepartmentSourceFilter(identity, {
                lenientTypes: activeDepartmentsSlug === 'caba',
              });
              const pieces = map.querySourceFeatures(ACTIVE_DEPARTMENT_SOURCE_ID, {
                sourceLayer: activeDepartmentsSourceLayer,
                filter: sourceFilter,
              });
              if (pieces && pieces.length > 0) {
                const b = new maplibregl.LngLatBounds();
                pieces.forEach((p) => {
                  const pb = boundsFromFeatureGeometry(p.geometry);
                  if (pb && !pb.isEmpty()) {
                    b.extend(pb.getSouthWest());
                    b.extend(pb.getNorthEast());
                  }
                });
                if (!b.isEmpty()) return b;
              }
            } catch {
              // fallback abajo
            }
          }
          return boundsFromFeatureGeometry(feature?.geometry);
        };
        getMunicipalityBounds = (feature, identity) => {
          if (activeMunicipalitiesSourceLayer && identity) {
            try {
              const sourceFilter = buildMunicipalitySourceFilter(identity, {
                lenientTypes: activeMunicipalitiesSlug === 'caba',
              });
              const pieces = map.querySourceFeatures(ACTIVE_MUNICIPALITY_SOURCE_ID, {
                sourceLayer: activeMunicipalitiesSourceLayer,
                filter: sourceFilter,
              });
              if (pieces && pieces.length > 0) {
                const b = new maplibregl.LngLatBounds();
                pieces.forEach((p) => {
                  const pb = boundsFromFeatureGeometry(p.geometry);
                  if (pb && !pb.isEmpty()) {
                    b.extend(pb.getSouthWest());
                    b.extend(pb.getNorthEast());
                  }
                });
                if (!b.isEmpty()) return b;
              }
            } catch {
              // fallback abajo
            }
          }
          return boundsFromFeatureGeometry(feature?.geometry);
        };
        getPedaniaBounds = (feature, identity) => {
          if (activePedaniasSourceLayer && identity) {
            try {
              const pieces = map.querySourceFeatures(ACTIVE_PEDANIA_SOURCE_ID, {
                sourceLayer: activePedaniasSourceLayer,
                filter: buildPedaniaFilter(identity),
              });
              if (pieces && pieces.length > 0) {
                const b = new maplibregl.LngLatBounds();
                pieces.forEach((p) => {
                  const pb = boundsFromFeatureGeometry(p.geometry);
                  if (pb && !pb.isEmpty()) {
                    b.extend(pb.getSouthWest());
                    b.extend(pb.getNorthEast());
                  }
                });
                if (!b.isEmpty()) return b;
              }
            } catch {
              // fallback abajo
            }
          }
          return boundsFromFeatureGeometry(feature?.geometry);
        };
        applyPedaniaLayerFilters = () => {
          if (map.getLayer(ACTIVE_PEDANIA_HIT_LAYER_ID)) {
            map.setFilter(ACTIVE_PEDANIA_HIT_LAYER_ID, pedaniaDepartmentScopeFilter);
          }
          if (map.getLayer(ACTIVE_PEDANIA_BORDERS_LAYER_ID)) {
            map.setFilter(ACTIVE_PEDANIA_BORDERS_LAYER_ID, pedaniaDepartmentScopeFilter);
          }
          if (map.getLayer(ACTIVE_PEDANIA_HOVER_FILL_LAYER_ID)) {
            map.setFilter(ACTIVE_PEDANIA_HOVER_FILL_LAYER_ID, ['all', pedaniaDepartmentScopeFilter, hoveredPedaniaFilter]);
          }
          if (map.getLayer(ACTIVE_PEDANIA_HOVER_LAYER_ID)) {
            map.setFilter(ACTIVE_PEDANIA_HOVER_LAYER_ID, ['all', pedaniaDepartmentScopeFilter, hoveredPedaniaFilter]);
          }
          if (map.getLayer(ACTIVE_PEDANIA_SELECTED_LAYER_ID)) {
            map.setFilter(ACTIVE_PEDANIA_SELECTED_LAYER_ID, ['all', pedaniaDepartmentScopeFilter, selectedPedaniaFilter]);
          }
        };
        applyMunicipalityLayerFilters = () => {
          const isCordobaPedaniaFlow =
            activeDepartmentsSlug === 'cordoba'
            && municipalityParentScopeFilter !== FILTER_NEVER_MATCH;
          const municipalityBaseScope = isCordobaPedaniaFlow
            ? municipalityParentScopeFilter
            : ['all', municipalityDepartmentScopeFilter, municipalityParentScopeFilter];

          if (map.getLayer(ACTIVE_MUNICIPALITY_HIT_LAYER_ID)) {
            map.setFilter(ACTIVE_MUNICIPALITY_HIT_LAYER_ID, municipalityBaseScope);
          }
          if (map.getLayer(ACTIVE_MUNICIPALITY_BORDERS_LAYER_ID)) {
            map.setFilter(ACTIVE_MUNICIPALITY_BORDERS_LAYER_ID, municipalityBaseScope);
          }
          if (map.getLayer(ACTIVE_MUNICIPALITY_HOVER_FILL_LAYER_ID)) {
            map.setFilter(ACTIVE_MUNICIPALITY_HOVER_FILL_LAYER_ID, [
              'all',
              municipalityBaseScope,
              hoveredMunicipalityFilter,
            ]);
          }
          if (map.getLayer(ACTIVE_MUNICIPALITY_HOVER_LAYER_ID)) {
            map.setFilter(ACTIVE_MUNICIPALITY_HOVER_LAYER_ID, [
              'all',
              municipalityBaseScope,
              hoveredMunicipalityFilter,
            ]);
          }
          if (map.getLayer(ACTIVE_MUNICIPALITY_SELECTED_LAYER_ID)) {
            map.setFilter(ACTIVE_MUNICIPALITY_SELECTED_LAYER_ID, [
              'all',
              municipalityBaseScope,
              selectedMunicipalityFilter,
            ]);
          }
        };
        removeMunicipalityLayersAndSource = () => {
          clearMunicipalityHover();
          clearMunicipalitySelection();
          [
            ACTIVE_MUNICIPALITY_SELECTED_LAYER_ID,
            ACTIVE_MUNICIPALITY_HOVER_LAYER_ID,
            ACTIVE_MUNICIPALITY_HOVER_FILL_LAYER_ID,
            ACTIVE_MUNICIPALITY_BORDERS_LAYER_ID,
            ACTIVE_MUNICIPALITY_HIT_LAYER_ID,
          ].forEach((layerId) => {
            if (map.getLayer(layerId)) map.removeLayer(layerId);
          });
          if (map.getSource(ACTIVE_MUNICIPALITY_SOURCE_ID)) {
            map.removeSource(ACTIVE_MUNICIPALITY_SOURCE_ID);
          }
          activeMunicipalitiesSourceLayer = null;
          activeMunicipalitiesOriginalUrl = null;
          activeMunicipalitiesSlug = null;
          municipalityDepartmentScopeFilter = FILTER_NEVER_MATCH;
          municipalityParentScopeFilter = FILTER_NEVER_MATCH;
          hoveredMunicipalityFilter = FILTER_NEVER_MATCH;
        };
        removeNeighborhoodLayersAndSource = () => {
          if (cancelled || map !== mapInstanceRef.current) return;
          try {
            [
              ACTIVE_NEIGHBORHOOD_LABELS_LAYER_ID,
              ACTIVE_NEIGHBORHOOD_BORDERS_LAYER_ID,
              ACTIVE_NEIGHBORHOOD_HIT_LAYER_ID,
            ].forEach((layerId) => {
              if (map.getLayer(layerId)) map.removeLayer(layerId);
            });
            if (map.getSource(ACTIVE_NEIGHBORHOOD_SOURCE_ID)) {
              map.removeSource(ACTIVE_NEIGHBORHOOD_SOURCE_ID);
            }
            if (map.getSource(ACTIVE_NEIGHBORHOOD_LABELS_SOURCE_ID)) {
              map.removeSource(ACTIVE_NEIGHBORHOOD_LABELS_SOURCE_ID);
            }
          } catch {
            // El mapa puede estar destruido si un async termina tras desmontar el componente.
          }
          activeNeighborhoodsSourceLayer = null;
          activeNeighborhoodsOriginalUrl = null;
          activeNeighborhoodsSlug = null;
        };
        removePedaniaLayersAndSource = () => {
          clearPedaniaHover();
          clearPedaniaSelection();
          [
            ACTIVE_PEDANIA_SELECTED_LAYER_ID,
            ACTIVE_PEDANIA_HOVER_LAYER_ID,
            ACTIVE_PEDANIA_HOVER_FILL_LAYER_ID,
            ACTIVE_PEDANIA_BORDERS_LAYER_ID,
            ACTIVE_PEDANIA_HIT_LAYER_ID,
          ].forEach((layerId) => {
            if (map.getLayer(layerId)) map.removeLayer(layerId);
          });
          if (map.getSource(ACTIVE_PEDANIA_SOURCE_ID)) {
            map.removeSource(ACTIVE_PEDANIA_SOURCE_ID);
          }
          activePedaniasSourceLayer = null;
          activePedaniasOriginalUrl = null;
          activePedaniasSlug = null;
          pedaniaDepartmentScopeFilter = FILTER_NEVER_MATCH;
          hoveredPedaniaFilter = FILTER_NEVER_MATCH;
        };
        removeBaDepartmentLayersAndSource = () => {
          clearDepartmentHover();
          clearDepartmentSelection();
          removePedaniaLayersAndSource();
          removeMunicipalityLayersAndSource();
          [ACTIVE_DEPARTMENT_SELECTED_LAYER_ID, ACTIVE_DEPARTMENT_HOVER_LAYER_ID, ACTIVE_DEPARTMENT_HOVER_FILL_LAYER_ID, ACTIVE_DEPARTMENT_BORDERS_LAYER_ID, ACTIVE_DEPARTMENT_HIT_LAYER_ID]
            .forEach((layerId) => {
              if (map.getLayer(layerId)) map.removeLayer(layerId);
            });
          if (map.getSource(ACTIVE_DEPARTMENT_SOURCE_ID)) {
            map.removeSource(ACTIVE_DEPARTMENT_SOURCE_ID);
          }
          activeDepartmentsSourceLayer = null;
          activeDepartmentsOriginalUrl = null;
          activeDepartmentsSlug = null;
        };
        clearAdministrativeSelectionRef.current = () => {
          clearPedaniaHover();
          clearPedaniaSelection();
          clearMunicipalityHover();
          clearMunicipalitySelection();
          clearDepartmentHover();
          clearDepartmentSelection();
          clearProvinceSelection();
          removeBaDepartmentLayersAndSource();
          selectedProvinceSnapshot = null;
          selectedDepartmentSnapshot = null;
          selectedPedaniaSnapshot = null;
          selectedMunicipalitySnapshot = null;
          if (onProvinceSelectionChangeRef.current) onProvinceSelectionChangeRef.current(null);
        };
        resetAdministrativeViewRef.current = () => {
          clearAdministrativeSelectionRef.current();
          const zoom = map.getZoom();
          // Cerca (barrio / manzana): solo quitar el chip y el resaltado.
          if (Number.isFinite(zoom) && zoom > ADMIN_CLEAR_KEEP_CAMERA_ABOVE_ZOOM) {
            return true;
          }
          map.stop();
          // Vuelta suave a la vista inicial.
          map.easeTo({
            center: INITIAL_MAP_VIEW.center,
            zoom: 3.5,
            duration: 2400,
            easing: (t) => 1 - ((1 - t) * (1 - t) * (1 - t)),
          });
          return false;
        };
        focusAdministrativeLevelRef.current = (level) => {
          if (!map) return false;
          const normalizeBounds = (boundsLike) => {
            if (!boundsLike) return null;
            if (typeof boundsLike.isEmpty === 'function') return boundsLike;
            try {
              return new maplibregl.LngLatBounds(boundsLike);
            } catch {
              return null;
            }
          };
          const fitSnapshotBounds = (snapshot, maxZoomFallback) => {
            const bounds = normalizeBounds(snapshot?.bounds);
            if (!bounds || bounds.isEmpty()) return false;
            const administrativeFitTopInset = MAP_BASE_STYLE.administrativeFitTopInset ?? 0;
            map.stop();
            map.fitBounds(bounds, {
              padding: { top: 48 + administrativeFitTopInset, right: 48, bottom: 48, left: 48 },
              maxZoom: maxZoomFallback,
              duration: 800,
            });
            return true;
          };

          if (level === 'province' && selectedProvinceSnapshot) {
            clearPedaniaHover();
            clearPedaniaSelection();
            clearMunicipalityHover();
            clearMunicipalitySelection();
            clearDepartmentHover();
            clearDepartmentSelection();
            removePedaniaLayersAndSource();
            removeMunicipalityLayersAndSource();
            pedaniaDepartmentScopeFilter = FILTER_NEVER_MATCH;
            municipalityDepartmentScopeFilter = FILTER_NEVER_MATCH;
            municipalityParentScopeFilter = FILTER_NEVER_MATCH;
            selectedDepartmentSnapshot = null;
            selectedPedaniaSnapshot = null;
            selectedMunicipalitySnapshot = null;
            if (onProvinceSelectionChangeRef.current) onProvinceSelectionChangeRef.current(selectedProvinceSnapshot.name);
            if (onDepartmentSelectionChangeRef.current) onDepartmentSelectionChangeRef.current(null);
            if (onPedaniaSelectionChangeRef.current) onPedaniaSelectionChangeRef.current(null);
            if (onMunicipalitySelectionChangeRef.current) onMunicipalitySelectionChangeRef.current(null);
            selectedProvinceFilter = selectedProvinceSnapshot.filter ?? PROVINCE_HOVER_INACTIVE;
            if (map.getLayer('provinces-admin-borders')) {
              map.setFilter('provinces-admin-borders', ['!', selectedProvinceFilter]);
            }
            if (map.getLayer('provinces-admin-hover-borders')) {
              map.setFilter('provinces-admin-hover-borders', selectedProvinceFilter);
            }
            moveProvinceSelectedBorderToTop(map);
            const provinceBounds = normalizeBounds(selectedProvinceSnapshot.bounds);
            if (!provinceBounds || provinceBounds.isEmpty()) return false;
            const administrativeFitTopInset = MAP_BASE_STYLE.administrativeFitTopInset ?? 0;
            const provinceFitPadding = selectedProvinceSnapshot.fitPadding ?? 56;
            map.stop();
            map.fitBounds(provinceBounds, {
              padding: {
                top: provinceFitPadding + administrativeFitTopInset,
                right: provinceFitPadding,
                bottom: provinceFitPadding,
                left: provinceFitPadding,
              },
              maxZoom: selectedProvinceSnapshot.fitMaxZoom ?? MAP_BASE_STYLE.provinceFitMaxZoom,
              duration: 800,
            });
            return true;
          }

          if (level === 'department' && selectedDepartmentSnapshot) {
            clearProvinceSelection();
            clearPedaniaHover();
            clearPedaniaSelection();
            clearMunicipalityHover();
            clearMunicipalitySelection();
            selectedPedaniaSnapshot = null;
            selectedMunicipalitySnapshot = null;
            selectedDepartmentFilter = buildDepartmentFilter(selectedDepartmentSnapshot.identity);
            if (map.getLayer(ACTIVE_DEPARTMENT_SELECTED_LAYER_ID)) {
              map.setFilter(ACTIVE_DEPARTMENT_SELECTED_LAYER_ID, selectedDepartmentFilter);
            }
            pedaniaDepartmentScopeFilter = buildPedaniaScopeFilter(selectedDepartmentSnapshot.identity);
            municipalityDepartmentScopeFilter = buildMunicipalityDepartmentScopeFilter(selectedDepartmentSnapshot.identity);
            const supportsPedanias = provinceSupportsAdministrativeLayer(activeDepartmentsSlug, 'pedanias');
            municipalityParentScopeFilter = supportsPedanias ? FILTER_NEVER_MATCH : municipalityDepartmentScopeFilter;
            applyPedaniaLayerFilters();
            applyMunicipalityLayerFilters();
            if (supportsPedanias && activeDepartmentsSlug) {
              const pedaniasOriginalUrl = buildPedaniasOriginalUrlForProvinceSlug(activeDepartmentsSlug);
              if (pedaniasOriginalUrl) void ensurePedaniaLayers(activeDepartmentsSlug, pedaniasOriginalUrl);
              removeMunicipalityLayersAndSource();
            } else if (activeDepartmentsSlug) {
              const municipalitiesOriginalUrl = buildMunicipalitiesOriginalUrlForProvinceSlug(activeDepartmentsSlug);
              if (municipalitiesOriginalUrl) void ensureMunicipalityLayers(activeDepartmentsSlug, municipalitiesOriginalUrl);
            }
            if (onDepartmentSelectionChangeRef.current) onDepartmentSelectionChangeRef.current(selectedDepartmentSnapshot.name);
            if (onPedaniaSelectionChangeRef.current) onPedaniaSelectionChangeRef.current(null);
            if (onMunicipalitySelectionChangeRef.current) onMunicipalitySelectionChangeRef.current(null);
            return fitSnapshotBounds(selectedDepartmentSnapshot, MAP_BASE_STYLE.departmentFitMaxZoom);
          }

          if (level === 'pedania' && selectedPedaniaSnapshot) {
            clearProvinceSelection();
            selectedDepartmentFilter = FILTER_NEVER_MATCH;
            if (map.getLayer(ACTIVE_DEPARTMENT_SELECTED_LAYER_ID)) {
              map.setFilter(ACTIVE_DEPARTMENT_SELECTED_LAYER_ID, FILTER_NEVER_MATCH);
            }
            clearMunicipalityHover();
            clearMunicipalitySelection();
            selectedMunicipalitySnapshot = null;
            selectedPedaniaFilter = buildPedaniaFilter(selectedPedaniaSnapshot.identity);
            municipalityParentScopeFilter = buildMunicipalityPedaniaScopeFilter(
              selectedPedaniaSnapshot.identity,
              selectedPedaniaSnapshot.props
            );
            applyPedaniaLayerFilters();
            applyMunicipalityLayerFilters();
            movePedaniaSelectedBorderToTop(map);
            if (onPedaniaSelectionChangeRef.current) onPedaniaSelectionChangeRef.current(selectedPedaniaSnapshot.name);
            if (onMunicipalitySelectionChangeRef.current) onMunicipalitySelectionChangeRef.current(null);
            return fitSnapshotBounds(selectedPedaniaSnapshot, MAP_BASE_STYLE.municipalityFitMaxZoom ?? 15.5);
          }

          if (level === 'municipality' && selectedMunicipalitySnapshot) {
            clearProvinceSelection();
            selectedDepartmentFilter = FILTER_NEVER_MATCH;
            if (map.getLayer(ACTIVE_DEPARTMENT_SELECTED_LAYER_ID)) {
              map.setFilter(ACTIVE_DEPARTMENT_SELECTED_LAYER_ID, FILTER_NEVER_MATCH);
            }
            selectedPedaniaFilter = FILTER_NEVER_MATCH;
            applyPedaniaLayerFilters();
            selectedMunicipalityFilter = buildMunicipalityFilter(selectedMunicipalitySnapshot.identity);
            applyMunicipalityLayerFilters();
            if (onMunicipalitySelectionChangeRef.current) {
              onMunicipalitySelectionChangeRef.current(selectedMunicipalitySnapshot.name);
            }
            return fitSnapshotBounds(selectedMunicipalitySnapshot, MAP_BASE_STYLE.municipalityFitMaxZoom);
          }

          return false;
        };
        ensureBaDepartmentLayers = async (provinceSlug, originalUrl) => {
          try {
            const signed = await getSignedPmtilesUrl(originalUrl);
            if (!signed) return false;
            if (cancelled || map !== mapInstanceRef.current || !canAccessMapStyle(map)) return false;
            if (map.getSource(ACTIVE_DEPARTMENT_SOURCE_ID) && activeDepartmentsSlug && activeDepartmentsSlug !== provinceSlug) {
              removeBaDepartmentLayersAndSource();
            }
            if (!map.getSource(ACTIVE_DEPARTMENT_SOURCE_ID)) {
              map.addSource(ACTIVE_DEPARTMENT_SOURCE_ID, {
                type: 'vector',
                url: signed,
                minzoom: 4,
                // No forzamos maxzoom: usamos el metadata real del PMTiles
                // para evitar pedir z inexistentes y perder render al acercar.
                promoteId: { departments: 'department_id' },
              });
              activeDepartmentsSlug = provinceSlug;
              activeDepartmentsOriginalUrl = originalUrl;
              activeDepartmentsSourceLayer = null;
            }

            if (!activeDepartmentsSourceLayer) {
              const candidates = [...DEPARTMENT_SOURCE_LAYER_CANDIDATES, `${provinceSlug}_departments`];
              for (const candidate of candidates) {
                try {
                  if (!map.getLayer(ACTIVE_DEPARTMENT_HIT_LAYER_ID)) {
                    map.addLayer({
                      id: ACTIVE_DEPARTMENT_HIT_LAYER_ID,
                      type: 'fill',
                      source: ACTIVE_DEPARTMENT_SOURCE_ID,
                      'source-layer': candidate,
                      paint: { 'fill-color': '#000000', 'fill-opacity': 0.01 },
                    });
                  }
                  activeDepartmentsSourceLayer = candidate;
                  break;
                } catch {
                  if (map.getLayer(ACTIVE_DEPARTMENT_HIT_LAYER_ID)) map.removeLayer(ACTIVE_DEPARTMENT_HIT_LAYER_ID);
                }
              }
            }
            if (!activeDepartmentsSourceLayer) return false;

            if (!map.getLayer(ACTIVE_DEPARTMENT_BORDERS_LAYER_ID)) {
              map.addLayer({
                id: ACTIVE_DEPARTMENT_BORDERS_LAYER_ID,
                type: 'line',
                source: ACTIVE_DEPARTMENT_SOURCE_ID,
                'source-layer': activeDepartmentsSourceLayer,
                paint: {
                  'line-color': getAdministrativeBordersColorForView(mapBaseViewIdRef.current),
                  'line-width': 1.2,
                  'line-opacity': 0.95,
                },
              });
            }
            if (!map.getLayer(ACTIVE_DEPARTMENT_HOVER_FILL_LAYER_ID)) {
              map.addLayer({
                id: ACTIVE_DEPARTMENT_HOVER_FILL_LAYER_ID,
                type: 'fill',
                source: ACTIVE_DEPARTMENT_SOURCE_ID,
                'source-layer': activeDepartmentsSourceLayer,
                paint: {
                  'fill-color': MAP_BASE_STYLE.administrativeHoverFillColor,
                  'fill-opacity': [
                    'interpolate', ['linear'], ['zoom'],
                    MAP_BASE_STYLE.administrativeHoverFillFadeStartZoom, MAP_BASE_STYLE.provincesHoverFillOpacity,
                    MAP_BASE_STYLE.administrativeHoverFillFadeEndZoom, 0,
                  ],
                },
                filter: FILTER_NEVER_MATCH,
              });
            }
            if (!map.getLayer(ACTIVE_DEPARTMENT_HOVER_LAYER_ID)) {
              map.addLayer({
                id: ACTIVE_DEPARTMENT_HOVER_LAYER_ID,
                type: 'line',
                source: ACTIVE_DEPARTMENT_SOURCE_ID,
                'source-layer': activeDepartmentsSourceLayer,
                paint: {
                  'line-color': MAP_BASE_STYLE.administrativeHoverColor,
                  'line-width': MAP_BASE_STYLE.administrativeHoverLineWidth,
                  'line-opacity': 1,
                },
                filter: FILTER_NEVER_MATCH,
              });
            }
            if (!map.getLayer(ACTIVE_DEPARTMENT_SELECTED_LAYER_ID)) {
              map.addLayer({
                id: ACTIVE_DEPARTMENT_SELECTED_LAYER_ID,
                type: 'line',
                source: ACTIVE_DEPARTMENT_SOURCE_ID,
                'source-layer': activeDepartmentsSourceLayer,
                paint: {
                  // El seleccionado mantiene el mismo look que el hover, pero persistente.
                  'line-color': MAP_BASE_STYLE.administrativeHoverColor,
                  'line-width': MAP_BASE_STYLE.administrativeSelectedLineWidth,
                  'line-opacity': 1,
                },
                filter: selectedDepartmentFilter,
              });
            }
            moveDepartmentLayersToTop(map);
            moveProvinceLabelsToTop(map);
            return true;
          } catch {
            removeBaDepartmentLayersAndSource();
            return false;
          }
        };
        ensureMunicipalityLayers = async (provinceSlug, originalUrl) => {
          try {
            const signed = await getSignedPmtilesUrl(originalUrl);
            if (!signed) return false;
            if (cancelled || map !== mapInstanceRef.current || !canAccessMapStyle(map)) return false;
            if (
              map.getSource(ACTIVE_MUNICIPALITY_SOURCE_ID)
              && activeMunicipalitiesSlug
              && activeMunicipalitiesSlug !== provinceSlug
            ) {
              removeMunicipalityLayersAndSource();
            }

            if (!map.getSource(ACTIVE_MUNICIPALITY_SOURCE_ID)) {
              map.addSource(ACTIVE_MUNICIPALITY_SOURCE_ID, {
                type: 'vector',
                url: signed,
                minzoom: 4,
              });
              activeMunicipalitiesSlug = provinceSlug;
              activeMunicipalitiesOriginalUrl = originalUrl;
              activeMunicipalitiesSourceLayer = null;
            }

            if (!activeMunicipalitiesSourceLayer) {
              const candidates = [...MUNICIPALITY_SOURCE_LAYER_CANDIDATES, `${provinceSlug}_municipalities`];
              for (const candidate of candidates) {
                try {
                  if (!map.getLayer(ACTIVE_MUNICIPALITY_HIT_LAYER_ID)) {
                    map.addLayer({
                      id: ACTIVE_MUNICIPALITY_HIT_LAYER_ID,
                      type: 'fill',
                      source: ACTIVE_MUNICIPALITY_SOURCE_ID,
                      'source-layer': candidate,
                      paint: { 'fill-color': '#000000', 'fill-opacity': 0.01 },
                    });
                  }
                  activeMunicipalitiesSourceLayer = candidate;
                  if (provinceSlug === 'cordoba') {
                    console.debug('[admin-debug] municipalities source-layer detected:', candidate);
                  }
                  break;
                } catch {
                  if (map.getLayer(ACTIVE_MUNICIPALITY_HIT_LAYER_ID)) map.removeLayer(ACTIVE_MUNICIPALITY_HIT_LAYER_ID);
                }
              }
            }
            if (!activeMunicipalitiesSourceLayer) return false;

            if (!map.getLayer(ACTIVE_MUNICIPALITY_BORDERS_LAYER_ID)) {
              map.addLayer({
                id: ACTIVE_MUNICIPALITY_BORDERS_LAYER_ID,
                type: 'line',
                source: ACTIVE_MUNICIPALITY_SOURCE_ID,
                'source-layer': activeMunicipalitiesSourceLayer,
                paint: {
                  'line-color': getAdministrativeBordersColorForView(mapBaseViewIdRef.current),
                  'line-width': 1.1,
                  'line-opacity': 0.95,
                },
              });
            }
            if (!map.getLayer(ACTIVE_MUNICIPALITY_HOVER_FILL_LAYER_ID)) {
              map.addLayer({
                id: ACTIVE_MUNICIPALITY_HOVER_FILL_LAYER_ID,
                type: 'fill',
                source: ACTIVE_MUNICIPALITY_SOURCE_ID,
                'source-layer': activeMunicipalitiesSourceLayer,
                paint: {
                  'fill-color': MAP_BASE_STYLE.administrativeHoverFillColor,
                  'fill-opacity': [
                    'interpolate', ['linear'], ['zoom'],
                    MAP_BASE_STYLE.administrativeHoverFillFadeStartZoom, MAP_BASE_STYLE.provincesHoverFillOpacity,
                    MAP_BASE_STYLE.administrativeHoverFillFadeEndZoom, 0,
                  ],
                },
                filter: ['all', municipalityDepartmentScopeFilter, hoveredMunicipalityFilter],
              });
            }
            if (!map.getLayer(ACTIVE_MUNICIPALITY_HOVER_LAYER_ID)) {
              map.addLayer({
                id: ACTIVE_MUNICIPALITY_HOVER_LAYER_ID,
                type: 'line',
                source: ACTIVE_MUNICIPALITY_SOURCE_ID,
                'source-layer': activeMunicipalitiesSourceLayer,
                paint: {
                  'line-color': MAP_BASE_STYLE.administrativeHoverColor,
                  'line-width': MAP_BASE_STYLE.administrativeHoverLineWidth,
                  'line-opacity': 1,
                },
                filter: ['all', municipalityDepartmentScopeFilter, hoveredMunicipalityFilter],
              });
            }
            if (!map.getLayer(ACTIVE_MUNICIPALITY_SELECTED_LAYER_ID)) {
              map.addLayer({
                id: ACTIVE_MUNICIPALITY_SELECTED_LAYER_ID,
                type: 'line',
                source: ACTIVE_MUNICIPALITY_SOURCE_ID,
                'source-layer': activeMunicipalitiesSourceLayer,
                paint: {
                  'line-color': MAP_BASE_STYLE.administrativeHoverColor,
                  'line-width': MAP_BASE_STYLE.administrativeSelectedLineWidth,
                  'line-opacity': 1,
                },
                filter: ['all', municipalityDepartmentScopeFilter, selectedMunicipalityFilter],
              });
            }
            applyMunicipalityLayerFilters();
            moveMunicipalityLayersToTop(map);
            movePedaniaLayersToTop(map);
            moveDepartmentLayersToTop(map);
            moveProvinceLabelsToTop(map);
            return true;
          } catch {
            removeMunicipalityLayersAndSource();
            return false;
          }
        };
        ensurePedaniaLayers = async (provinceSlug, originalUrl) => {
          try {
            const signed = await getSignedPmtilesUrl(originalUrl);
            if (!signed) return false;
            if (cancelled || map !== mapInstanceRef.current || !canAccessMapStyle(map)) return false;
            if (
              map.getSource(ACTIVE_PEDANIA_SOURCE_ID)
              && activePedaniasSlug
              && activePedaniasSlug !== provinceSlug
            ) {
              removePedaniaLayersAndSource();
            }

            if (!map.getSource(ACTIVE_PEDANIA_SOURCE_ID)) {
              map.addSource(ACTIVE_PEDANIA_SOURCE_ID, {
                type: 'vector',
                url: signed,
                minzoom: 4,
              });
              activePedaniasSlug = provinceSlug;
              activePedaniasOriginalUrl = originalUrl;
              activePedaniasSourceLayer = null;
            }

            if (!activePedaniasSourceLayer) {
              const candidates = [...PEDANIA_SOURCE_LAYER_CANDIDATES, `${provinceSlug}_pedanias`];
              for (const candidate of candidates) {
                try {
                  if (!map.getLayer(ACTIVE_PEDANIA_HIT_LAYER_ID)) {
                    map.addLayer({
                      id: ACTIVE_PEDANIA_HIT_LAYER_ID,
                      type: 'fill',
                      source: ACTIVE_PEDANIA_SOURCE_ID,
                      'source-layer': candidate,
                      paint: { 'fill-color': '#000000', 'fill-opacity': 0.01 },
                    });
                  }
                  activePedaniasSourceLayer = candidate;
                  if (provinceSlug === 'cordoba') {
                    console.debug('[admin-debug] pedanias source-layer detected:', candidate);
                  }
                  break;
                } catch {
                  if (map.getLayer(ACTIVE_PEDANIA_HIT_LAYER_ID)) map.removeLayer(ACTIVE_PEDANIA_HIT_LAYER_ID);
                }
              }
            }
            if (!activePedaniasSourceLayer) return false;

            if (!map.getLayer(ACTIVE_PEDANIA_BORDERS_LAYER_ID)) {
              map.addLayer({
                id: ACTIVE_PEDANIA_BORDERS_LAYER_ID,
                type: 'line',
                source: ACTIVE_PEDANIA_SOURCE_ID,
                'source-layer': activePedaniasSourceLayer,
                paint: {
                  'line-color': getAdministrativeBordersColorForView(mapBaseViewIdRef.current),
                  'line-width': 1.8,
                  'line-opacity': 1,
                },
              });
            }
            if (!map.getLayer(ACTIVE_PEDANIA_HOVER_FILL_LAYER_ID)) {
              map.addLayer({
                id: ACTIVE_PEDANIA_HOVER_FILL_LAYER_ID,
                type: 'fill',
                source: ACTIVE_PEDANIA_SOURCE_ID,
                'source-layer': activePedaniasSourceLayer,
                paint: {
                  'fill-color': MAP_BASE_STYLE.administrativeHoverFillColor,
                  'fill-opacity': [
                    'interpolate', ['linear'], ['zoom'],
                    MAP_BASE_STYLE.administrativeHoverFillFadeStartZoom, MAP_BASE_STYLE.provincesHoverFillOpacity,
                    MAP_BASE_STYLE.administrativeHoverFillFadeEndZoom, 0,
                  ],
                },
                filter: ['all', pedaniaDepartmentScopeFilter, hoveredPedaniaFilter],
              });
            }
            if (!map.getLayer(ACTIVE_PEDANIA_HOVER_LAYER_ID)) {
              map.addLayer({
                id: ACTIVE_PEDANIA_HOVER_LAYER_ID,
                type: 'line',
                source: ACTIVE_PEDANIA_SOURCE_ID,
                'source-layer': activePedaniasSourceLayer,
                paint: {
                  'line-color': MAP_BASE_STYLE.administrativeHoverColor,
                  'line-width': MAP_BASE_STYLE.administrativeHoverLineWidth,
                  'line-opacity': 1,
                },
                filter: ['all', pedaniaDepartmentScopeFilter, hoveredPedaniaFilter],
              });
            }
            if (!map.getLayer(ACTIVE_PEDANIA_SELECTED_LAYER_ID)) {
              map.addLayer({
                id: ACTIVE_PEDANIA_SELECTED_LAYER_ID,
                type: 'line',
                source: ACTIVE_PEDANIA_SOURCE_ID,
                'source-layer': activePedaniasSourceLayer,
                paint: {
                  'line-color': MAP_BASE_STYLE.administrativeHoverColor,
                  'line-width': MAP_BASE_STYLE.administrativeSelectedLineWidth,
                  'line-opacity': 1,
                },
                filter: ['all', pedaniaDepartmentScopeFilter, selectedPedaniaFilter],
              });
            }
            applyPedaniaLayerFilters();
            moveMunicipalityLayersToTop(map);
            movePedaniaLayersToTop(map);
            moveDepartmentLayersToTop(map);
            moveProvinceLabelsToTop(map);
            return true;
          } catch {
            removePedaniaLayersAndSource();
            return false;
          }
        };
        ensureNeighborhoodLayers = async (provinceSlug, originalUrl) => {
          try {
            if (!canAccessMapStyle(map)) return false;
            if (
              map.getSource(ACTIVE_NEIGHBORHOOD_SOURCE_ID)
              && activeNeighborhoodsSlug
              && activeNeighborhoodsSlug === provinceSlug
            ) {
              syncNeighborhoodLabelsSource();
              moveNeighborhoodLayersToTop(map);
              return true;
            }

            const signed = await getSignedPmtilesUrl(originalUrl);
            if (!signed) return false;
            if (cancelled || map !== mapInstanceRef.current || !canAccessMapStyle(map)) return false;
            if (
              map.getSource(ACTIVE_NEIGHBORHOOD_SOURCE_ID)
              && activeNeighborhoodsSlug
              && activeNeighborhoodsSlug !== provinceSlug
            ) {
              removeNeighborhoodLayersAndSource();
            }

            if (!map.getSource(ACTIVE_NEIGHBORHOOD_SOURCE_ID)) {
              map.addSource(ACTIVE_NEIGHBORHOOD_SOURCE_ID, {
                type: 'vector',
                url: signed,
                minzoom: 10,
              });
              activeNeighborhoodsSlug = provinceSlug;
              activeNeighborhoodsOriginalUrl = originalUrl;
              activeNeighborhoodsSourceLayer = null;
            }

            if (!activeNeighborhoodsSourceLayer) {
              const candidates = [...NEIGHBORHOOD_SOURCE_LAYER_CANDIDATES, `${provinceSlug}_neighborhoods`];
              for (const candidate of candidates) {
                try {
                  if (!map.getLayer(ACTIVE_NEIGHBORHOOD_HIT_LAYER_ID)) {
                    map.addLayer({
                      id: ACTIVE_NEIGHBORHOOD_HIT_LAYER_ID,
                      type: 'fill',
                      source: ACTIVE_NEIGHBORHOOD_SOURCE_ID,
                      'source-layer': candidate,
                      minzoom: NEIGHBORHOOD_NAMES_MIN_ZOOM,
                      maxzoom: NEIGHBORHOOD_NAMES_MAX_ZOOM,
                      paint: { 'fill-color': '#000000', 'fill-opacity': 0.01 },
                    });
                  }
                  if (!map.getLayer(ACTIVE_NEIGHBORHOOD_BORDERS_LAYER_ID)) {
                    map.addLayer({
                      id: ACTIVE_NEIGHBORHOOD_BORDERS_LAYER_ID,
                      type: 'line',
                      source: ACTIVE_NEIGHBORHOOD_SOURCE_ID,
                      'source-layer': candidate,
                      minzoom: NEIGHBORHOOD_NAMES_MIN_ZOOM,
                      paint: {
                        'line-color': getNeighborhoodBordersColorForView(mapBaseViewIdRef.current),
                        'line-width': [
                          'interpolate', ['linear'], ['zoom'],
                          NEIGHBORHOOD_NAMES_MIN_ZOOM,
                          MAP_BASE_STYLE.neighborhoodBorderWidthByZoom?.min ?? 0.9,
                          NEIGHBORHOOD_NAMES_MIN_ZOOM + (MAP_BASE_STYLE.neighborhoodBorderWidthByZoom?.zoomSpan ?? 4),
                          MAP_BASE_STYLE.neighborhoodBorderWidthByZoom?.max ?? 1.5,
                        ],
                        'line-opacity': MAP_BASE_STYLE.neighborhoodBorderOpacity ?? 0.92,
                      },
                    });
                  }
                  activeNeighborhoodsSourceLayer = candidate;
                  syncNeighborhoodLabelsSource();
                  if (NEIGHBORHOOD_NAME_RENDER_MODE === 'labels' && !map.getLayer(ACTIVE_NEIGHBORHOOD_LABELS_LAYER_ID)) {
                    map.addLayer({
                      id: ACTIVE_NEIGHBORHOOD_LABELS_LAYER_ID,
                      type: 'symbol',
                      source: ACTIVE_NEIGHBORHOOD_LABELS_SOURCE_ID,
                      minzoom: NEIGHBORHOOD_NAMES_MIN_ZOOM,
                      maxzoom: NEIGHBORHOOD_NAMES_MAX_ZOOM,
                      layout: {
                        'text-field': ['get', 'name'],
                        'symbol-placement': 'point',
                        'symbol-avoid-edges': true,
                        visibility: 'visible',
                        'text-font': ['Open Sans Bold'],
                        'text-size': [
                          'interpolate', ['linear'], ['zoom'],
                          NEIGHBORHOOD_NAMES_MIN_ZOOM, 10.8,
                          NEIGHBORHOOD_NAMES_MAX_ZOOM, 12,
                        ],
                        'text-max-width': 7,
                        'text-letter-spacing': 0.01,
                        'text-padding': 10,
                        'text-keep-upright': true,
                        'text-allow-overlap': false,
                        'text-ignore-placement': false,
                      },
                      paint: {
                        'text-color': '#ffffff',
                        'text-halo-color': 'rgba(0,0,0,0.75)',
                        'text-halo-width': 2,
                        'text-halo-blur': 0.5,
                      },
                    });
                  }
                  break;
                } catch {
                  [
                    ACTIVE_NEIGHBORHOOD_LABELS_LAYER_ID,
                    ACTIVE_NEIGHBORHOOD_BORDERS_LAYER_ID,
                    ACTIVE_NEIGHBORHOOD_HIT_LAYER_ID,
                  ].forEach((layerId) => {
                    if (map.getLayer(layerId)) map.removeLayer(layerId);
                  });
                  if (map.getSource(ACTIVE_NEIGHBORHOOD_LABELS_SOURCE_ID)) {
                    map.removeSource(ACTIVE_NEIGHBORHOOD_LABELS_SOURCE_ID);
                  }
                }
              }
            }
            if (!activeNeighborhoodsSourceLayer) return false;

            moveNeighborhoodLayersToTop(map);
            moveMunicipalityLayersToTop(map);
            movePedaniaLayersToTop(map);
            moveDepartmentLayersToTop(map);
            moveProvinceLabelsToTop(map);
            return true;
          } catch {
            removeNeighborhoodLayersAndSource();
            return false;
          }
        };

        const mountProvincesAdmin = (signedUrl) => {
          if (!signedUrl || !canAccessMapStyle(map)) return false;
          if (!map.getSource('provinces-admin')) {
            map.addSource('provinces-admin', {
              type: 'vector',
              url: signedUrl,
              minzoom: 3,
              maxzoom: 10,
              // Alinea el id de feature con el id de provincia en el PMTiles (ver logs: province_id).
              // Así feature.id, ['id'] en filtros y resaltado por hover coinciden con todos los retazos de tile.
              promoteId: { provinces: 'province_id' },
            });
          }

          // Relleno casi transparente: toda la provincia es “clickeable” y dispara hover (el borde solo no alcanza).
          if (!map.getLayer('provinces-admin-hit-area')) {
            map.addLayer({
              id: 'provinces-admin-hit-area',
              type: 'fill',
              source: 'provinces-admin',
              'source-layer': 'provinces',
              paint: {
                'fill-color': '#000000',
                'fill-opacity': 0.08,
              },
            });
          }

          if (!map.getLayer('provinces-admin-borders')) {
            map.addLayer({
              id: 'provinces-admin-borders',
              type: 'line',
              source: 'provinces-admin',
              'source-layer': 'provinces',
              paint: {
                'line-color':
                  mapBaseViewIdRef.current === 'street'
                    ? MAP_BASE_STYLE.provincesBorderColorStreet
                    : MAP_BASE_STYLE.provincesBorderColor,
                'line-width': MAP_BASE_STYLE.provincesBorderWidth,
                'line-dasharray': MAP_BASE_STYLE.provincesBorderDasharray,
              },
            });
          }

          if (!map.getLayer('provinces-admin-hover-fill')) {
            map.addLayer({
              id: 'provinces-admin-hover-fill',
              type: 'fill',
              source: 'provinces-admin',
              'source-layer': 'provinces',
              paint: {
                'fill-color': MAP_BASE_STYLE.provincesHoverFillColor ?? 'rgba(72,146,145,0.18)',
                'fill-opacity': MAP_BASE_STYLE.provincesHoverFillOpacity,
              },
              filter: PROVINCE_HOVER_INACTIVE,
            });
          }

          if (!map.getLayer('provinces-admin-hover-borders')) {
            map.addLayer({
              id: 'provinces-admin-hover-borders',
              type: 'line',
              source: 'provinces-admin',
              'source-layer': 'provinces',
              paint: {
                'line-color': MAP_BASE_STYLE.provincesHoverColor,
                'line-width': MAP_BASE_STYLE.provincesHoverWidth,
              },
              filter: PROVINCE_HOVER_INACTIVE,
            });
          }

          // `provinces-admin` fija maxzoom 10 y el archivo no tiene datos ahí: por
          // encima del último zoom real los tiles llegan vacíos. Esta fuente toma
          // el maxzoom de la cabecera y estira el último tile, para saber la
          // provincia del centro cuando un encuadre salta directo a zoom alto.
          if (!map.getSource(PROVINCES_LOOKUP_SOURCE_ID)) {
            map.addSource(PROVINCES_LOOKUP_SOURCE_ID, {
              type: 'vector',
              url: signedUrl,
            });
          }
          if (!map.getLayer(PROVINCES_LOOKUP_LAYER_ID)) {
            map.addLayer({
              id: PROVINCES_LOOKUP_LAYER_ID,
              type: 'fill',
              source: PROVINCES_LOOKUP_SOURCE_ID,
              'source-layer': 'provinces',
              minzoom: NEIGHBORHOOD_NAMES_MIN_ZOOM,
              paint: { 'fill-opacity': 0 },
            });
          }
          return true;
        };

        mountProvincesAdmin(provincesSignedUrl);
        if (!map.getSource('provinces-labels-src')) {
          map.addSource('provinces-labels-src', {
            type: 'geojson',
            data: PROVINCE_LABEL_GEOJSON,
          });
        }

        if (!map.getLayer('provinces-labels')) {
          map.addLayer({
            id: 'provinces-labels',
            type: 'symbol',
            source: 'provinces-labels-src',
            minzoom: 1,
            maxzoom: 10,
            layout: {
              'text-field': ['get', 'name'],
              'text-font': ['Open Sans Bold'],
              'text-anchor': 'center',
              'text-max-width': 8,
              'text-allow-overlap': false,
              'text-size': [
                'interpolate', ['linear'], ['zoom'],
                4, 8,
                8, 15,
                10, 18,
              ],
              // En vista Calle el raster ya trae información; ocultamos etiquetas propias.
              visibility: isStreetRasterView(mapBaseViewIdRef.current) ? 'none' : 'visible',
            },
            paint: {
              'text-color': '#ffffff',
              'text-halo-color': 'rgba(0,0,0,0.75)',
              'text-halo-width': 2,
              'text-halo-blur': 0.5,
            },
          });
        }

        const detachAdministrativeLoadListeners = () => {
          if (!administrativeLoadListenersAttached) return;
          map.off('sourcedata', handleAdministrativeLoadProgress);
          map.off('idle', handleAdministrativeLoadProgress);
          administrativeLoadListenersAttached = false;
        };

        const settleAdministrativeLoad = () => {
          if (administrativeLoadSettled) return;
          administrativeLoadSettled = true;
          if (administrativeLoadFallbackTimeout) {
            clearTimeout(administrativeLoadFallbackTimeout);
            administrativeLoadFallbackTimeout = null;
          }
          emitAdministrativeLoadState(false);
          detachAdministrativeLoadListeners();
        };

        handleAdministrativeLoadProgress = () => {
          if (administrativeLoadSettled) return;
          if (!map.getLayer('provinces-labels')) return;
          if (!map.getSource('provinces-labels-src')) return;
          if (!map.isSourceLoaded('provinces-labels-src')) return;

          if (map.getSource('provinces-admin')) {
            if (!map.getLayer('provinces-admin-borders')) return;
            if (!map.isSourceLoaded('provinces-admin')) return;
          }

          settleAdministrativeLoad();
        };

        map.on('sourcedata', handleAdministrativeLoadProgress);
        map.on('idle', handleAdministrativeLoadProgress);
        administrativeLoadListenersAttached = true;
        handleAdministrativeLoadProgress();

        moveProvinceLayersToTop(map);
        moveProvinceLabelsToTop(map);
        moveBaseLabelsToTop(map);

        if (cancelled || map !== mapInstanceRef.current) return;

        provincesPmtilesRefreshDebounced = debounce(() => {
          if (cancelled || map !== mapInstanceRef.current || !canAccessMapStyle(map)) return;
          moveNeighborhoodLayersToTop(map);
          syncNeighborhoodLabelsSource();
          const provinceSlugAtCenterResolved = provinceSlugAtCenter(map);
          const neighborhoodsOriginalUrl = buildNeighborhoodsOriginalUrlForProvinceSlug(provinceSlugAtCenterResolved);
          if (
            provinceSlugAtCenterResolved
            && provinceSupportsAdministrativeLayer(provinceSlugAtCenterResolved, 'neighborhoods')
            && neighborhoodsOriginalUrl
          ) {
            void ensureNeighborhoodLayers(provinceSlugAtCenterResolved, neighborhoodsOriginalUrl);
          }
          if (!map.getSource('provinces-admin')) {
            void getSignedPmtilesUrl(PROVINCES_ORIGINAL_URL).then((signed) => {
              if (cancelled || map !== mapInstanceRef.current || !canAccessMapStyle(map)) return;
              mountProvincesAdmin(signed);
            });
          } else {
            void refreshPmtilesVectorSourceIfNeeded(map, 'provinces-admin', PROVINCES_ORIGINAL_URL);
            void refreshPmtilesVectorSourceIfNeeded(map, PROVINCES_LOOKUP_SOURCE_ID, PROVINCES_ORIGINAL_URL);
          }
          if (map.getSource(ACTIVE_DEPARTMENT_SOURCE_ID) && activeDepartmentsOriginalUrl) {
            void refreshPmtilesVectorSourceIfNeeded(map, ACTIVE_DEPARTMENT_SOURCE_ID, activeDepartmentsOriginalUrl);
          }
          if (map.getSource(ACTIVE_MUNICIPALITY_SOURCE_ID) && activeMunicipalitiesOriginalUrl) {
            void refreshPmtilesVectorSourceIfNeeded(map, ACTIVE_MUNICIPALITY_SOURCE_ID, activeMunicipalitiesOriginalUrl);
          }
          if (map.getSource(ACTIVE_PEDANIA_SOURCE_ID) && activePedaniasOriginalUrl) {
            void refreshPmtilesVectorSourceIfNeeded(map, ACTIVE_PEDANIA_SOURCE_ID, activePedaniasOriginalUrl);
          }
          if (map.getSource(ACTIVE_NEIGHBORHOOD_SOURCE_ID) && activeNeighborhoodsOriginalUrl) {
            void refreshPmtilesVectorSourceIfNeeded(map, ACTIVE_NEIGHBORHOOD_SOURCE_ID, activeNeighborhoodsOriginalUrl);
          }
        }, 400);
        map.on('idle', provincesPmtilesRefreshDebounced);
        map.on('move', provincesPmtilesRefreshDebounced);
        map.on('zoom', provincesPmtilesRefreshDebounced);
        // El encuadre termina antes de que lleguen los tiles: reintentar cuando
        // cargan. Filtrar por fuente: el setData de etiquetas también emite sourcedata.
        neighborhoodTilesSourcedataHandler = (e) => {
          if (
            e?.sourceId !== PROVINCES_LOOKUP_SOURCE_ID
            && e?.sourceId !== ACTIVE_NEIGHBORHOOD_SOURCE_ID
          ) return;
          if (!e.tile) return;
          provincesPmtilesRefreshDebounced();
        };
        map.on('sourcedata', neighborhoodTilesSourcedataHandler);

        visibilityHandler = () => {
          if (document.visibilityState === 'visible') {
            if (!map.getSource('provinces-admin')) {
              void getSignedPmtilesUrl(PROVINCES_ORIGINAL_URL).then((signed) => {
                if (cancelled || map !== mapInstanceRef.current || !canAccessMapStyle(map)) return;
                mountProvincesAdmin(signed);
              });
            } else {
              void refreshPmtilesVectorSourceIfNeeded(map, 'provinces-admin', PROVINCES_ORIGINAL_URL);
            }
          }
        };
        document.addEventListener('visibilitychange', visibilityHandler);

        const clearProvinceHover = () => {
          hoveredProvinceName = null;
          map.getCanvas().style.cursor = '';
          if (map.getLayer('provinces-admin-hover-fill')) {
            map.setFilter('provinces-admin-hover-fill', PROVINCE_HOVER_INACTIVE);
          }
          if (map.getLayer('provinces-admin-hover-borders')) {
            map.setFilter(
              'provinces-admin-hover-borders',
              selectedProvinceFilter === PROVINCE_HOVER_INACTIVE
                ? PROVINCE_HOVER_INACTIVE
                : selectedProvinceFilter
            );
          }
        };
        clearProvinceSelection = () => {
          selectedProvinceFilter = PROVINCE_HOVER_INACTIVE;
          if (map.getLayer('provinces-admin-borders')) {
            map.setFilter('provinces-admin-borders', null);
          }
          if (map.getLayer('provinces-admin-hover-borders')) {
            map.setFilter('provinces-admin-hover-borders', PROVINCE_HOVER_INACTIVE);
          }
        };

        handleProvincePointerMove = (e) => {
          if (provinceHoverRaf !== null) cancelAnimationFrame(provinceHoverRaf);
          const pt = e.point;
          provinceHoverRaf = requestAnimationFrame(() => {
            provinceHoverRaf = null;
            // En zoom de parcelas, el cursor/hover lo maneja useParcelHover.
            // Evitamos pisarlo desde la lógica administrativa.
            if (map.getZoom() >= MAP_MIN_LOT_ZOOM) {
              clearPedaniaHover();
              clearMunicipalityHover();
              clearDepartmentHover();
              // Limpiamos solo filtros de hover de provincia sin tocar cursor,
              // para no interferir con el pointer de parcelas.
              hoveredProvinceName = null;
              if (map.getLayer('provinces-admin-hover-fill')) {
                map.setFilter('provinces-admin-hover-fill', PROVINCE_HOVER_INACTIVE);
              }
              if (map.getLayer('provinces-admin-hover-borders')) {
                map.setFilter(
                  'provinces-admin-hover-borders',
                  selectedProvinceFilter === PROVINCE_HOVER_INACTIVE
                    ? PROVINCE_HOVER_INACTIVE
                    : selectedProvinceFilter
                );
              }
              return;
            }
            const canShowNeighborhoodNames = (
              map.getZoom() >= NEIGHBORHOOD_NAMES_MIN_ZOOM
              && map.getZoom() <= NEIGHBORHOOD_NAMES_MAX_ZOOM
            );
            if (NEIGHBORHOOD_NAME_RENDER_MODE === 'tooltip' && canShowNeighborhoodNames) {
              const neighborhoodFeature = queryNeighborhoodFeatureAtPoint(pt);
              if (neighborhoodFeature) {
                const neighborhoodName =
                  (typeof neighborhoodFeature.properties?.neighborhood_name === 'string'
                    && neighborhoodFeature.properties.neighborhood_name.trim())
                  || (typeof neighborhoodFeature.properties?.barrio === 'string'
                    && neighborhoodFeature.properties.barrio.trim())
                  || (typeof neighborhoodFeature.properties?.name === 'string'
                    && neighborhoodFeature.properties.name.trim())
                  || null;
                if (neighborhoodName && e.lngLat) {
                  if (!departmentTooltipPopup) {
                    departmentTooltipPopup = new maplibregl.Popup({
                      closeButton: false,
                      closeOnClick: false,
                      offset: 12,
                      className: 'terrio-department-tooltip',
                    });
                  }
                  departmentTooltipPopup
                    .setLngLat(e.lngLat)
                    .setText(neighborhoodName)
                    .addTo(map);
                  map.getCanvas().style.cursor = 'pointer';
                  return;
                }
              }
            }
            if (canShowNeighborhoodNames) {
              const neighborhoodFeatureForBreadcrumb = queryNeighborhoodFeatureAtPoint(pt);
              const neighborhoodNameForBreadcrumb = neighborhoodFeatureForBreadcrumb
                ? (
                  (typeof neighborhoodFeatureForBreadcrumb.properties?.neighborhood_name === 'string'
                    && neighborhoodFeatureForBreadcrumb.properties.neighborhood_name.trim())
                  || (typeof neighborhoodFeatureForBreadcrumb.properties?.barrio === 'string'
                    && neighborhoodFeatureForBreadcrumb.properties.barrio.trim())
                  || (typeof neighborhoodFeatureForBreadcrumb.properties?.name === 'string'
                    && neighborhoodFeatureForBreadcrumb.properties.name.trim())
                  || null
                )
                : null;
              setNeighborhoodHoverName(neighborhoodNameForBreadcrumb);
            } else {
              setNeighborhoodHoverName(null);
            }
            const municipalityFeature = queryMunicipalityFeatureAtPoint(pt);
            if (municipalityFeature) {
              clearProvinceHover();
              clearDepartmentHover();
              clearPedaniaHover();
              const identity = getMunicipalityIdentityForFilter(municipalityFeature.properties);
              const idStr = identity ? `${identity.key}\0${identity.value}` : null;
              if (idStr && idStr !== hoveredMunicipalityKey) {
                hoveredMunicipalityKey = idStr;
                hoveredMunicipalityFilter = buildMunicipalityFilter(identity);
                applyMunicipalityLayerFilters();
              }
              const municipalityName =
                (typeof municipalityFeature.properties?.municipality_name === 'string' && municipalityFeature.properties.municipality_name.trim()) ||
                (typeof municipalityFeature.properties?.municipio === 'string' && municipalityFeature.properties.municipio.trim()) ||
                (typeof municipalityFeature.properties?.name === 'string' && municipalityFeature.properties.name.trim()) ||
                null;
              const tooltipMaxZoom = getMunicipalityTooltipMaxZoom(activeMunicipalitiesSlug);
              const shouldShowTooltip = map.getZoom() <= tooltipMaxZoom;
              if (municipalityName && e.lngLat && shouldShowTooltip) {
                if (!departmentTooltipPopup) {
                  departmentTooltipPopup = new maplibregl.Popup({
                    closeButton: false,
                    closeOnClick: false,
                    offset: 12,
                    className: 'terrio-department-tooltip',
                  });
                }
                departmentTooltipPopup
                  .setLngLat(e.lngLat)
                  .setText(municipalityName)
                  .addTo(map);
              } else if (departmentTooltipPopup) {
                departmentTooltipPopup.remove();
                departmentTooltipPopup = null;
              }
              map.getCanvas().style.cursor = 'pointer';
              return;
            }
            clearMunicipalityHover();
            const pedaniaFeature = queryPedaniaFeatureAtPoint(pt);
            if (pedaniaFeature) {
              clearProvinceHover();
              clearDepartmentHover();
              const identity = getPedaniaIdentityForFilter(pedaniaFeature.properties);
              const idStr = identity ? `${identity.key}\0${identity.value}` : null;
              if (idStr && idStr !== hoveredPedaniaKey) {
                hoveredPedaniaKey = idStr;
                hoveredPedaniaFilter = buildPedaniaFilter(identity);
                applyPedaniaLayerFilters();
              }
              const pedaniaName =
                (typeof pedaniaFeature.properties?.pedania_name === 'string' && pedaniaFeature.properties.pedania_name.trim()) ||
                (typeof pedaniaFeature.properties?.pedania === 'string' && pedaniaFeature.properties.pedania.trim()) ||
                (typeof pedaniaFeature.properties?.name === 'string' && pedaniaFeature.properties.name.trim()) ||
                null;
              const tooltipMaxZoom = getDepartmentTooltipMaxZoom(activePedaniasSlug);
              const shouldShowTooltip = map.getZoom() <= tooltipMaxZoom;
              if (pedaniaName && e.lngLat && shouldShowTooltip) {
                if (!departmentTooltipPopup) {
                  departmentTooltipPopup = new maplibregl.Popup({
                    closeButton: false,
                    closeOnClick: false,
                    offset: 12,
                    className: 'terrio-department-tooltip',
                  });
                }
                departmentTooltipPopup
                  .setLngLat(e.lngLat)
                  .setText(pedaniaName)
                  .addTo(map);
              } else if (departmentTooltipPopup) {
                departmentTooltipPopup.remove();
                departmentTooltipPopup = null;
              }
              map.getCanvas().style.cursor = 'pointer';
              return;
            }
            clearPedaniaHover();
            const departmentFeature = queryDepartmentFeatureAtPoint(pt);
            if (departmentFeature) {
              // Si estamos hovereando un departamento, no debe quedar activo
              // el resaltado de alguna provincia previa.
              clearProvinceHover();
              const identity = getDepartmentIdentityForFilter(departmentFeature.properties);
              const idStr = identity ? `${identity.key}\0${identity.value}` : null;
              if (idStr && idStr !== hoveredDepartmentKey) {
                hoveredDepartmentKey = idStr;
                const nextFilter = buildDepartmentFilter(identity);
                if (map.getLayer(ACTIVE_DEPARTMENT_HOVER_FILL_LAYER_ID)) {
                  map.setFilter(ACTIVE_DEPARTMENT_HOVER_FILL_LAYER_ID, nextFilter);
                }
                if (map.getLayer(ACTIVE_DEPARTMENT_HOVER_LAYER_ID)) {
                  map.setFilter(ACTIVE_DEPARTMENT_HOVER_LAYER_ID, nextFilter);
                }
              }
              const departmentName =
                (typeof departmentFeature.properties?.department_name === 'string' && departmentFeature.properties.department_name.trim()) ||
                (typeof departmentFeature.properties?.name === 'string' && departmentFeature.properties.name.trim()) ||
                (typeof departmentFeature.properties?.depto === 'string' && departmentFeature.properties.depto.trim()) ||
                null;
              const tooltipMaxZoom = getDepartmentTooltipMaxZoom(activeDepartmentsSlug);
              const shouldShowDepartmentTooltip = map.getZoom() <= tooltipMaxZoom;
              if (departmentName && e.lngLat && shouldShowDepartmentTooltip) {
                if (!departmentTooltipPopup) {
                  departmentTooltipPopup = new maplibregl.Popup({
                    closeButton: false,
                    closeOnClick: false,
                    offset: 12,
                    className: 'terrio-department-tooltip',
                  });
                }
                departmentTooltipPopup
                  .setLngLat(e.lngLat)
                  .setText(departmentName)
                  .addTo(map);
              } else if (departmentTooltipPopup) {
                departmentTooltipPopup.remove();
                departmentTooltipPopup = null;
              }
              map.getCanvas().style.cursor = 'pointer';
              return;
            }
            clearDepartmentHover();
            if (map.getZoom() > PROVINCE_HOVER_MAX_ZOOM) {
              clearProvinceHover();
              return;
            }
            const f = queryProvinceFeatureAtPoint(map, pt);
            if (!f) {
              clearProvinceHover();
              return;
            }
            const identity = getProvinceIdentityForFilter(f.properties);
            if (f.id == null && !identity) {
              clearProvinceHover();
              return;
            }
            const idStr = f.id != null ? `id:${f.id}` : `${identity.key}\0${identity.value}`;
            if (idStr === hoveredProvinceName) return;
            map.getCanvas().style.cursor = 'pointer';
            const flt = buildProvinceHoverFilter(f, identity);
            hoveredProvinceName = idStr;
            if (map.getLayer('provinces-admin-hover-fill')) map.setFilter('provinces-admin-hover-fill', flt);
            if (map.getLayer('provinces-admin-hover-borders')) map.setFilter('provinces-admin-hover-borders', flt);
          });
        };

        handlePointerLeaveCanvas = () => {
          if (provinceHoverRaf !== null) {
            cancelAnimationFrame(provinceHoverRaf);
            provinceHoverRaf = null;
          }
          clearPedaniaHover();
          clearMunicipalityHover();
          clearDepartmentHover();
          clearProvinceHover();
          setNeighborhoodHoverName(null);
        };

        map.on('mousemove', handleProvincePointerMove);
        map.getCanvas().addEventListener('mouseleave', handlePointerLeaveCanvas);
      })();
    });

    mapInstanceRef.current.on('move', () => {
      if (onMove) onMove(mapInstanceRef.current);
    });

    mapInstanceRef.current.on('zoom', () => {
      if (onMove) onMove(mapInstanceRef.current);
      // const map = mapInstanceRef.current;
      // if (!map) return;

      // if (onMove) onMove(map);

      // Crossfade satélite → street para evitar ver tiles "Map data not yet available" - Revisar opción
      // const zoom = map.getZoom();
      // const fadeStart = 16;
      // const fadeEnd   = 17.0;

      // let satelliteOpacity = 1;

      // if (zoom > fadeStart) {
      //   const t = Math.min(1, (zoom - fadeStart) / (fadeEnd - fadeStart));
      //   satelliteOpacity = 1 - t;
      // }

      // const streetOpacity = 1 - satelliteOpacity;

      // map.setPaintProperty('satellite-layer', 'raster-opacity', satelliteOpacity);
      // map.setPaintProperty('street-layer',    'raster-opacity', streetOpacity);
    });

    mapInstanceRef.current.on('click', (event) => {
      const m = mapInstanceRef.current;
      if (!m) return;

      if (m.getZoom() < MAP_MIN_LOT_ZOOM && m.getLayer('provinces-admin-hit-area')) {
        const municipalityFeature = m.getLayer(ACTIVE_MUNICIPALITY_HIT_LAYER_ID)
          ? m.queryRenderedFeatures(event.point, { layers: [ACTIVE_MUNICIPALITY_HIT_LAYER_ID] })[0]
          : null;
        if (municipalityFeature) {
          const municipalityIdentity = getMunicipalityIdentityForFilter(municipalityFeature.properties);
          const municipalityName =
            (typeof municipalityFeature.properties?.municipality_name === 'string' && municipalityFeature.properties.municipality_name.trim()) ||
            (typeof municipalityFeature.properties?.municipio === 'string' && municipalityFeature.properties.municipio.trim()) ||
            (typeof municipalityFeature.properties?.name === 'string' && municipalityFeature.properties.name.trim()) ||
            null;
          if (municipalityIdentity) {
            clearProvinceSelection();
            selectedDepartmentFilter = FILTER_NEVER_MATCH;
            if (m.getLayer(ACTIVE_DEPARTMENT_SELECTED_LAYER_ID)) {
              m.setFilter(ACTIVE_DEPARTMENT_SELECTED_LAYER_ID, FILTER_NEVER_MATCH);
            }
            selectedPedaniaFilter = FILTER_NEVER_MATCH;
            applyPedaniaLayerFilters();
            selectedMunicipalityFilter = buildMunicipalityFilter(municipalityIdentity);
            applyMunicipalityLayerFilters();
          }
          if (onMunicipalitySelectionChangeRef.current) onMunicipalitySelectionChangeRef.current(municipalityName);
          const municipalityBounds = getMunicipalityBounds(municipalityFeature, municipalityIdentity);
          selectedMunicipalitySnapshot = {
            identity: municipalityIdentity,
            name: municipalityName,
            bounds: municipalityBounds,
          };
          if (municipalityBounds && !municipalityBounds.isEmpty()) {
            const isCabaMunicipality = activeMunicipalitiesSlug === 'caba';
            const municipalityFitMaxZoom =
              isCabaMunicipality
                ? (MAP_BASE_STYLE.cabaMunicipalityFitMaxZoom ?? MAP_BASE_STYLE.municipalityFitMaxZoom)
                : MAP_BASE_STYLE.municipalityFitMaxZoom;
            const municipalityFitPadding =
              isCabaMunicipality
                ? (MAP_BASE_STYLE.cabaMunicipalityFitPadding ?? 48)
                : 48;
            const administrativeFitTopInset = MAP_BASE_STYLE.administrativeFitTopInset ?? 0;
            m.fitBounds(municipalityBounds, {
              padding: {
                top: municipalityFitPadding + administrativeFitTopInset,
                right: municipalityFitPadding,
                bottom: municipalityFitPadding,
                left: municipalityFitPadding,
              },
              maxZoom: municipalityFitMaxZoom,
              duration: 900,
            });
          }
          return;
        }

        const pedaniaFeature = m.getLayer(ACTIVE_PEDANIA_HIT_LAYER_ID)
          ? m.queryRenderedFeatures(event.point, { layers: [ACTIVE_PEDANIA_HIT_LAYER_ID] })[0]
          : null;
        if (pedaniaFeature) {
          const pedaniaIdentity = getPedaniaIdentityForFilter(pedaniaFeature.properties);
          const pedaniaName =
            (typeof pedaniaFeature.properties?.pedania_name === 'string' && pedaniaFeature.properties.pedania_name.trim()) ||
            (typeof pedaniaFeature.properties?.pedania === 'string' && pedaniaFeature.properties.pedania.trim()) ||
            (typeof pedaniaFeature.properties?.name === 'string' && pedaniaFeature.properties.name.trim()) ||
            null;
          if (pedaniaIdentity) {
            clearProvinceSelection();
            selectedDepartmentFilter = FILTER_NEVER_MATCH;
            if (m.getLayer(ACTIVE_DEPARTMENT_SELECTED_LAYER_ID)) {
              m.setFilter(ACTIVE_DEPARTMENT_SELECTED_LAYER_ID, FILTER_NEVER_MATCH);
            }
            selectedPedaniaFilter = buildPedaniaFilter(pedaniaIdentity);
            applyPedaniaLayerFilters();
            municipalityParentScopeFilter = buildMunicipalityPedaniaScopeFilter(
              pedaniaIdentity,
              pedaniaFeature.properties
            );
            const applyCordobaMunicipalityScopeFromPedania = () => {
              if (!(activeMunicipalitiesSourceLayer && activePedaniasSlug === 'cordoba')) return;
              try {
                const muniAll = m.querySourceFeatures(ACTIVE_MUNICIPALITY_SOURCE_ID, {
                  sourceLayer: activeMunicipalitiesSourceLayer,
                });
                const muniScoped = m.querySourceFeatures(ACTIVE_MUNICIPALITY_SOURCE_ID, {
                  sourceLayer: activeMunicipalitiesSourceLayer,
                  filter: municipalityParentScopeFilter,
                });
                if ((muniScoped?.length ?? 0) === 0) {
                  const pedaniaPieces = activePedaniasSourceLayer
                    ? m.querySourceFeatures(ACTIVE_PEDANIA_SOURCE_ID, {
                      sourceLayer: activePedaniasSourceLayer,
                      filter: buildPedaniaFilter(pedaniaIdentity),
                    })
                    : [];
                  const municipalitiesSpatialFilter = buildMunicipalitySpatialScopeFilterFromPedaniaFeatures(
                    muniAll || [],
                    pedaniaPieces || []
                  );
                  if (municipalitiesSpatialFilter !== FILTER_NEVER_MATCH) {
                    municipalityParentScopeFilter = municipalitiesSpatialFilter;
                  }
                }
              } catch (error) {
                console.debug('[admin-debug] cordoba municipalities debug failed:', error);
              }
            };
            const applyCordobaMunicipalityScopeAndRefresh = () => {
              applyCordobaMunicipalityScopeFromPedania();
              hoveredMunicipalityKey = null;
              hoveredMunicipalityFilter = FILTER_NEVER_MATCH;
              selectedMunicipalityFilter = FILTER_NEVER_MATCH;
              applyMunicipalityLayerFilters();
            };
            const retryCordobaMunicipalityScopeAndRefresh = (retriesLeft = 8) => {
              applyCordobaMunicipalityScopeAndRefresh();
              if (!(activeMunicipalitiesSourceLayer && activePedaniasSlug === 'cordoba')) {
                if (retriesLeft > 0) {
                  setTimeout(() => retryCordobaMunicipalityScopeAndRefresh(retriesLeft - 1), 250);
                }
                return;
              }
              try {
                const scoped = m.querySourceFeatures(ACTIVE_MUNICIPALITY_SOURCE_ID, {
                  sourceLayer: activeMunicipalitiesSourceLayer,
                  filter: municipalityParentScopeFilter,
                });
                if ((scoped?.length ?? 0) > 0 || retriesLeft <= 0) return;
              } catch {
                if (retriesLeft <= 0) return;
              }
              setTimeout(() => retryCordobaMunicipalityScopeAndRefresh(retriesLeft - 1), 250);
            };
            if (!m.getSource(ACTIVE_MUNICIPALITY_SOURCE_ID) && activePedaniasSlug === 'cordoba') {
              const municipalitiesOriginalUrl = buildMunicipalitiesOriginalUrlForProvinceSlug(activePedaniasSlug);
              if (municipalitiesOriginalUrl) {
                void ensureMunicipalityLayers(activePedaniasSlug, municipalitiesOriginalUrl).then((ensured) => {
                  if (!ensured) return;
                  applyCordobaMunicipalityScopeAndRefresh();
                  retryCordobaMunicipalityScopeAndRefresh();
                  // En el primer click puede no haber tiles de municipios listos aún.
                  // Reintenta cuando el mapa quede idle para evitar exigir un segundo click.
                  m.once('idle', () => {
                    applyCordobaMunicipalityScopeAndRefresh();
                    retryCordobaMunicipalityScopeAndRefresh();
                  });
                });
              }
            } else {
              applyCordobaMunicipalityScopeAndRefresh();
              if (activePedaniasSlug === 'cordoba') {
                retryCordobaMunicipalityScopeAndRefresh();
              }
              if (activePedaniasSlug === 'cordoba') {
                m.once('idle', () => {
                  applyCordobaMunicipalityScopeAndRefresh();
                  retryCordobaMunicipalityScopeAndRefresh();
                });
              }
            }
            const neighborhoodsOriginalUrl = buildNeighborhoodsOriginalUrlForProvinceSlug(activePedaniasSlug);
            if (
              activePedaniasSlug
              && provinceSupportsAdministrativeLayer(activePedaniasSlug, 'neighborhoods')
              && neighborhoodsOriginalUrl
            ) {
              void ensureNeighborhoodLayers(activePedaniasSlug, neighborhoodsOriginalUrl);
            }
            movePedaniaSelectedBorderToTop(m);
          }
          if (onPedaniaSelectionChangeRef.current) onPedaniaSelectionChangeRef.current(pedaniaName);
          if (onMunicipalitySelectionChangeRef.current) onMunicipalitySelectionChangeRef.current(null);
          const pedaniaBounds = getPedaniaBounds(pedaniaFeature, pedaniaIdentity);
          selectedPedaniaSnapshot = {
            identity: pedaniaIdentity,
            name: pedaniaName,
            bounds: pedaniaBounds,
            props: pedaniaFeature.properties,
          };
          selectedMunicipalitySnapshot = null;
          if (pedaniaBounds && !pedaniaBounds.isEmpty()) {
            const administrativeFitTopInset = MAP_BASE_STYLE.administrativeFitTopInset ?? 0;
            m.fitBounds(pedaniaBounds, {
              padding: { top: 48 + administrativeFitTopInset, right: 48, bottom: 48, left: 48 },
              maxZoom: MAP_BASE_STYLE.municipalityFitMaxZoom ?? 15.5,
              duration: 900,
            });
          }
          return;
        }

        const departmentFeature = m.getLayer(ACTIVE_DEPARTMENT_HIT_LAYER_ID)
          ? m.queryRenderedFeatures(event.point, { layers: [ACTIVE_DEPARTMENT_HIT_LAYER_ID] })[0]
          : null;
        if (departmentFeature) {
          const departmentIdentity = getDepartmentIdentityForFilter(departmentFeature.properties);
          const departmentName =
            (typeof departmentFeature.properties?.department_name === 'string' && departmentFeature.properties.department_name.trim()) ||
            (typeof departmentFeature.properties?.name === 'string' && departmentFeature.properties.name.trim()) ||
            (typeof departmentFeature.properties?.depto === 'string' && departmentFeature.properties.depto.trim()) ||
            null;
          if (departmentIdentity) {
            clearProvinceSelection();
            selectedDepartmentFilter = buildDepartmentFilter(departmentIdentity);
            if (m.getLayer(ACTIVE_DEPARTMENT_SELECTED_LAYER_ID)) {
              m.setFilter(ACTIVE_DEPARTMENT_SELECTED_LAYER_ID, selectedDepartmentFilter);
            }
          }
          if (onDepartmentSelectionChangeRef.current) onDepartmentSelectionChangeRef.current(departmentName);
          pedaniaDepartmentScopeFilter = buildPedaniaScopeFilter(departmentIdentity);
          hoveredPedaniaKey = null;
          hoveredPedaniaFilter = FILTER_NEVER_MATCH;
          selectedPedaniaFilter = FILTER_NEVER_MATCH;
          applyPedaniaLayerFilters();
          municipalityDepartmentScopeFilter = buildMunicipalityDepartmentScopeFilter(departmentIdentity);
          const supportsPedanias = provinceSupportsAdministrativeLayer(activeDepartmentsSlug, 'pedanias');
          municipalityParentScopeFilter = supportsPedanias ? FILTER_NEVER_MATCH : municipalityDepartmentScopeFilter;
          hoveredMunicipalityKey = null;
          hoveredMunicipalityFilter = FILTER_NEVER_MATCH;
          selectedMunicipalityFilter = FILTER_NEVER_MATCH;
          applyMunicipalityLayerFilters();
          const pedaniasOriginalUrl = buildPedaniasOriginalUrlForProvinceSlug(activeDepartmentsSlug);
          if (supportsPedanias && activeDepartmentsSlug && pedaniasOriginalUrl) {
            void ensurePedaniaLayers(activeDepartmentsSlug, pedaniasOriginalUrl);
            // En provincias con jerarquía extra (ej. Córdoba), municipios se habilitan
            // recién al seleccionar una pedanía para respetar escala de interacción.
            removeMunicipalityLayersAndSource();
          } else {
            removePedaniaLayersAndSource();
            municipalityParentScopeFilter = municipalityDepartmentScopeFilter;
            applyMunicipalityLayerFilters();
            const neighborhoodsOriginalUrl = buildNeighborhoodsOriginalUrlForProvinceSlug(activeDepartmentsSlug);
            if (
              activeDepartmentsSlug
              && provinceSupportsAdministrativeLayer(activeDepartmentsSlug, 'neighborhoods')
              && neighborhoodsOriginalUrl
            ) {
              void ensureNeighborhoodLayers(activeDepartmentsSlug, neighborhoodsOriginalUrl);
            }
            if (onPedaniaSelectionChangeRef.current) onPedaniaSelectionChangeRef.current(null);
          }
          const municipalitiesOriginalUrl = buildMunicipalitiesOriginalUrlForProvinceSlug(activeDepartmentsSlug);
          const supportsMunicipalities = provinceSupportsAdministrativeLayer(activeDepartmentsSlug, 'municipalities');
          if (
            supportsMunicipalities
            && activeDepartmentsSlug
            && municipalitiesOriginalUrl
            && !supportsPedanias
          ) {
            void ensureMunicipalityLayers(activeDepartmentsSlug, municipalitiesOriginalUrl);
          } else {
            removeMunicipalityLayersAndSource();
          }
          if (onPedaniaSelectionChangeRef.current) onPedaniaSelectionChangeRef.current(null);
          if (onMunicipalitySelectionChangeRef.current) onMunicipalitySelectionChangeRef.current(null);
          const departmentBounds = getDepartmentBounds(departmentFeature, departmentIdentity);
          selectedDepartmentSnapshot = {
            identity: departmentIdentity,
            name: departmentName,
            bounds: departmentBounds,
          };
          selectedPedaniaSnapshot = null;
          selectedMunicipalitySnapshot = null;
          if (departmentBounds && !departmentBounds.isEmpty()) {
            const isCabaDepartment = activeDepartmentsSlug === 'caba';
            const departmentFitMaxZoom =
              isCabaDepartment
                ? (MAP_BASE_STYLE.cabaDepartmentFitMaxZoom ?? MAP_BASE_STYLE.departmentFitMaxZoom)
                : MAP_BASE_STYLE.departmentFitMaxZoom;
            const departmentFitPadding =
              isCabaDepartment
                ? (MAP_BASE_STYLE.cabaDepartmentFitPadding ?? 52)
                : 52;
            const administrativeFitTopInset = MAP_BASE_STYLE.administrativeFitTopInset ?? 0;
            m.fitBounds(departmentBounds, {
              padding: {
                top: departmentFitPadding + administrativeFitTopInset,
                right: departmentFitPadding,
                bottom: departmentFitPadding,
                left: departmentFitPadding,
              },
              maxZoom: departmentFitMaxZoom,
              duration: 900,
            });
          }
          return;
        }

        const f = queryProvinceFeatureAtPoint(m, event.point);
        if (f) {
          const provinceIdentity = getProvinceIdentityForFilter(f.properties);
          const provinceName = getProvinceNameFromProps(f.properties) || null;
          const provinceSlug = getProvinceSlugFromProps(f.properties);
          const departmentsOriginalUrl = buildDepartmentsOriginalUrlForProvinceSlug(provinceSlug);
          const neighborhoodsOriginalUrl = buildNeighborhoodsOriginalUrlForProvinceSlug(provinceSlug);
          if (provinceSlug && departmentsOriginalUrl) {
            void ensureBaDepartmentLayers(provinceSlug, departmentsOriginalUrl);
          } else {
            removeBaDepartmentLayersAndSource();
          }
          if (
            provinceSlug
            && provinceSupportsAdministrativeLayer(provinceSlug, 'neighborhoods')
            && neighborhoodsOriginalUrl
          ) {
            void ensureNeighborhoodLayers(provinceSlug, neighborhoodsOriginalUrl);
          }
          removeMunicipalityLayersAndSource();
          removePedaniaLayersAndSource();
          pedaniaDepartmentScopeFilter = FILTER_NEVER_MATCH;
          municipalityDepartmentScopeFilter = FILTER_NEVER_MATCH;
          municipalityParentScopeFilter = FILTER_NEVER_MATCH;
          if (onProvinceSelectionChangeRef.current) onProvinceSelectionChangeRef.current(provinceName);
          if (onDepartmentSelectionChangeRef.current) onDepartmentSelectionChangeRef.current(null);
          if (onPedaniaSelectionChangeRef.current) onPedaniaSelectionChangeRef.current(null);
          if (onMunicipalitySelectionChangeRef.current) onMunicipalitySelectionChangeRef.current(null);
          selectedDepartmentFilter = FILTER_NEVER_MATCH;
          if (m.getLayer(ACTIVE_DEPARTMENT_SELECTED_LAYER_ID)) {
            m.setFilter(ACTIVE_DEPARTMENT_SELECTED_LAYER_ID, FILTER_NEVER_MATCH);
          }
          selectedPedaniaFilter = FILTER_NEVER_MATCH;
          selectedMunicipalityFilter = FILTER_NEVER_MATCH;
          applyPedaniaLayerFilters();
          applyMunicipalityLayerFilters();
          selectedProvinceFilter = buildProvinceHoverFilter(f, provinceIdentity);
          if (m.getLayer('provinces-admin-borders')) {
            m.setFilter('provinces-admin-borders', ['!', selectedProvinceFilter]);
          }
          if (m.getLayer('provinces-admin-hover-borders')) {
            m.setFilter('provinces-admin-hover-borders', selectedProvinceFilter);
          }
          moveProvinceSelectedBorderToTop(m);
          const manualBounds = getProvinceManualBounds(f.properties);
          const dynamicBounds = boundsFromFeatureGeometry(f.geometry);
          const targetBounds = manualBounds || dynamicBounds;
          selectedProvinceSnapshot = {
            name: provinceName,
            slug: provinceSlug,
            bounds: targetBounds,
            filter: selectedProvinceFilter,
          };
          selectedDepartmentSnapshot = null;
          selectedPedaniaSnapshot = null;
          selectedMunicipalitySnapshot = null;
          const provinceMaxZoomOverride = getProvinceFitMaxZoomOverride(f.properties);
          const isCabaProvince = isCabaProvinceSlug(provinceSlug);
          const provinceFitPadding = isCabaProvince
            ? (MAP_BASE_STYLE.cabaProvinceFitPadding ?? 56)
            : 56;
          const provinceFitMaxZoom = provinceMaxZoomOverride
            ?? (isCabaProvince
              ? (MAP_BASE_STYLE.cabaProvinceFitMaxZoom ?? MAP_BASE_STYLE.provinceFitMaxZoom)
              : MAP_BASE_STYLE.provinceFitMaxZoom);
          if (targetBounds) {
            const administrativeFitTopInset = MAP_BASE_STYLE.administrativeFitTopInset ?? 0;
            m.fitBounds(targetBounds, {
              padding: {
                top: provinceFitPadding + administrativeFitTopInset,
                right: provinceFitPadding,
                bottom: provinceFitPadding,
                left: provinceFitPadding,
              },
              maxZoom: provinceFitMaxZoom,
              duration: 900,
            });
          }
          selectedProvinceSnapshot.fitPadding = provinceFitPadding;
          selectedProvinceSnapshot.fitMaxZoom = provinceFitMaxZoom;
          return;
        }
        return;
      }

      if (onMapClick) onMapClick(event, m);
    });

    return () => {
      cancelled = true;
      if (onNeighborhoodHoverChangeRef.current) onNeighborhoodHoverChangeRef.current(null);
      clearAdministrativeSelectionRef.current = () => {};
      resetAdministrativeViewRef.current = () => {};
      focusAdministrativeLevelRef.current = () => false;
      if (visibilityHandler) {
        document.removeEventListener('visibilitychange', visibilityHandler);
      }
      if (mapInstanceRef.current) {
        emitAdministrativeLoadState(false);
        if (handleProvincePointerMove) mapInstanceRef.current.off('mousemove', handleProvincePointerMove);
        if (handlePointerLeaveCanvas) mapInstanceRef.current.getCanvas().removeEventListener('mouseleave', handlePointerLeaveCanvas);
        if (provinceHoverRaf !== null) {
          cancelAnimationFrame(provinceHoverRaf);
          provinceHoverRaf = null;
        }
        if (administrativeLoadFallbackTimeout) {
          clearTimeout(administrativeLoadFallbackTimeout);
          administrativeLoadFallbackTimeout = null;
        }
        if (administrativeLoadListenersAttached) {
          mapInstanceRef.current.off('sourcedata', handleAdministrativeLoadProgress);
          mapInstanceRef.current.off('idle', handleAdministrativeLoadProgress);
          administrativeLoadListenersAttached = false;
        }
        if (departmentTooltipPopup) {
          departmentTooltipPopup.remove();
          departmentTooltipPopup = null;
        }
        if (provincesPmtilesRefreshDebounced) {
          mapInstanceRef.current.off('idle', provincesPmtilesRefreshDebounced);
          mapInstanceRef.current.off('move', provincesPmtilesRefreshDebounced);
          mapInstanceRef.current.off('zoom', provincesPmtilesRefreshDebounced);
        }
        if (neighborhoodTilesSourcedataHandler) {
          mapInstanceRef.current.off('sourcedata', neighborhoodTilesSourcedataHandler);
        }
        mapInstanceRef.current.remove();
        mapInstanceRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /**
   * Cambia la vista del mapa activando solo la capa seleccionada.
   * Calle / calle oscuro dependen de IGN: si no responden, no apagamos el satélite.
   *
   * @param {'satellite1'|'satellite2'|'street'|'streetDark'} viewId
   * @returns {Promise<boolean>} `false` si IGN no está y se dejó la vista anterior
   */
  const setMapView = async (viewId) => {
    const map = mapInstanceRef.current;
    if (!map) return false;

    if (isStreetRasterView(viewId)) {
      const ignOk = await probeIgnBasemap(viewId);
      // El probe es async: el mapa pudo destruirse o reemplazarse mientras tanto.
      if (map !== mapInstanceRef.current || !isMapAlive(map)) return false;
      if (!ignOk) {
        toast.error('Calle no está disponible ahora. Seguimos en satélite.');
        return false;
      }
    }

    applyBaseMapView(map, viewId);
    return true;
  };

  const applyBaseMapView = (map, viewId) => {
    mapBaseViewIdRef.current = viewId;

    if (isStreetRasterView(viewId)) {
      Object.keys(IGN_RASTER_VIEWS).forEach((id) => {
        if (id === viewId) return;
        const layerId = IGN_RASTER_VIEWS[id].layerId;
        if (!map.getLayer(layerId)) return;
        map.setLayoutProperty(layerId, 'visibility', 'none');
        map.setPaintProperty(layerId, 'raster-opacity', 0);
      });
      ensureIgnStreetRaster(map, viewId);
    } else {
      hideIgnStreetRasters(map);
    }

    const opacities = {
      'satellite1-layer': viewId === 'satellite1' ? 1 : 0,
      'satellite2-layer': viewId === 'satellite2' ? 1 : 0,
      'satellite-labels-layer': (viewId === 'satellite1' || viewId === 'satellite2') ? 0.9 : 0,
      'street-layer':     viewId === 'street'     ? 1 : 0,
      'street-dark-layer': viewId === 'streetDark' ? 1 : 0,
    };

    Object.entries(opacities).forEach(([layerId, opacity]) => {
      if (map.getLayer(layerId)) {
        map.setPaintProperty(layerId, 'raster-opacity', opacity);
        if (layerId === 'street-layer' || layerId === 'street-dark-layer') {
          map.setLayoutProperty(layerId, 'visibility', opacity > 0 ? 'visible' : 'none');
        }
      }
    });

    if (map.getLayer('provinces-labels')) {
      map.setLayoutProperty(
        'provinces-labels',
        'visibility',
        isStreetRasterView(viewId) ? 'none' : 'visible'
      );
    }

    // Overlay (rutas + etiquetas): por defecto se oculta en street/streetDark (IGN ya trae contexto).
    // Excepción: en streetDark el raster base viene sin labels, así que dejamos visibles los nombres de lugares.
    const overlayLayers = [
      'overlay-roads-major',
      'overlay-roads-minor',
      'overlay-streets',
      'overlay-place-labels',
      'overlay-road-labels',
      'overlay-street-labels',
    ];
    const isStreet = viewId === 'street';
    const shouldHideOverlay = isStreetRasterView(viewId);
    overlayLayers.forEach((id) => {
      if (map.getLayer(id)) {
        const visibility = id === 'overlay-place-labels'
          ? (isStreet ? 'none' : 'visible')
          : (shouldHideOverlay ? 'none' : 'visible');
        map.setLayoutProperty(id, 'visibility', visibility);
      }
    });

    if (map.getLayer(ACTIVE_NEIGHBORHOOD_LABELS_LAYER_ID)) {
      map.setLayoutProperty(
        ACTIVE_NEIGHBORHOOD_LABELS_LAYER_ID,
        'visibility',
        'visible'
      );
    }

    if (map.getLayer('provinces-admin-borders')) {
      map.setPaintProperty(
        'provinces-admin-borders',
        'line-color',
        viewId === 'streetDark'
          ? '#ffffff'
          : (viewId === 'street'
            ? MAP_BASE_STYLE.provincesBorderColorStreet
            : MAP_BASE_STYLE.provincesBorderColor)
      );
    }

    if (map.getLayer(ACTIVE_DEPARTMENT_BORDERS_LAYER_ID)) {
      map.setPaintProperty(
        ACTIVE_DEPARTMENT_BORDERS_LAYER_ID,
        'line-color',
        getAdministrativeBordersColorForView(viewId)
      );
    }
    if (map.getLayer(ACTIVE_MUNICIPALITY_BORDERS_LAYER_ID)) {
      map.setPaintProperty(
        ACTIVE_MUNICIPALITY_BORDERS_LAYER_ID,
        'line-color',
        getAdministrativeBordersColorForView(viewId)
      );
    }
    if (map.getLayer(ACTIVE_PEDANIA_BORDERS_LAYER_ID)) {
      map.setPaintProperty(
        ACTIVE_PEDANIA_BORDERS_LAYER_ID,
        'line-color',
        getAdministrativeBordersColorForView(viewId)
      );
    }
    if (map.getLayer(ACTIVE_NEIGHBORHOOD_BORDERS_LAYER_ID)) {
      map.setPaintProperty(
        ACTIVE_NEIGHBORHOOD_BORDERS_LAYER_ID,
        'line-color',
        getNeighborhoodBordersColorForView(viewId)
      );
    }
  };

  const clearAdministrativeSelection = () => {
    clearAdministrativeSelectionRef.current();
  };

  const resetAdministrativeView = () => {
    return resetAdministrativeViewRef.current();
  };

  const focusAdministrativeLevel = (level) => {
    return focusAdministrativeLevelRef.current(level);
  };

  return {
    mapRef,
    mapInstanceRef,
    mapUnavailable: mapUnavailableReason != null,
    mapUnavailableReason,
    setMapView,
    centerOnUserLocation,
    clearAdministrativeSelection,
    resetAdministrativeView,
    focusAdministrativeLevel,
  };
}